import React, { Suspense, lazy, useState, useEffect } from 'react';
import SurfaceScope from '../components/security/SurfaceScope';
import ThreatsToSignals from '../components/security/ThreatsToSignals';
// Old Code Security paths forward to the redesigned pages (see the redirects module).
import { RedirectKeepQuery as SASTRedirect, RedirectToNewScan as SASTRedirectNewScan, RedirectScan as SASTRedirectScan } from '../pages/sast/_shared/redirects';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { SESSION_CONTINUED_EVENT } from '../services/auth/sessionExpiry';
import AppLayout from '../components/layout/AppLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageLoadingFallback from '../components/common/PageLoadingFallback';
import ProtectedRoute from '../components/common/ProtectedRoute';
import UnknownRoute from './UnknownRoute';
import { SaasVendorProvider } from '../contexts/SaasVendorContext';
import SaasControlBar from '../components/saas-ai/SaasControlBar';
import SaasSurfaceOutlet from '../components/saas-ai/SaasSurfaceOutlet';
import SaasSecurityScope from '../components/saas-ai/SaasSecurityScope';
import GeminiAgentsLayout from '../pages/gemini/agents/GeminiAgentsLayout';
import GeminiWorkspaceLayout from '../pages/gemini/GeminiWorkspaceLayout';
import CopilotLayout from '../pages/copilot/CopilotLayout';
import { EndpointDateRangeProvider } from '../contexts/EndpointDateRangeContext';
import { DateRangeProvider } from '../contexts/DateRangeContext';
import { isFeatureEnabled } from '../utils/featureFlags';
import { getModuleLicense } from '../licensing/useModuleLicense';
import SaasVendorLockOutlet from '../components/saas-ai/SaasVendorLockOutlet';
import SaasAtlasWindow from '../components/saas-ai/SaasAtlasWindow';
import { CUSTOM_AGENTS_DASHBOARD_PATH } from '../components/layout/Sidebar/productConfig';
import { buildRedirectTarget } from './redirectTarget';
import ForwardEvaluationsTab from './ForwardEvaluationsTab';
import ExternalRedirect from './ExternalRedirect';
import EndpointPeopleRedirect from './EndpointPeopleRedirect';
import { DOCS_URL } from '../config/docs';
import CustomAgentsLayout from '../pages/custom-agents/CustomAgentsLayout';
import CustomAgentsTimeRange from '../pages/custom-agents/CustomAgentsTimeRange';
import { fallbackPathFor } from '../pages/custom-agents/customAgentsRoutes';
import { CUSTOM_AGENTS_MODULE } from '../pages/custom-agents/customAgentsModule';
import { isSwitched as isEndpointPageSwitched } from '../pages/shadow-ai/atlas/switch';

// Lazy load all page components for code splitting
const Login = lazy(() => import('../pages/login'));
// Public magic-link claim page for AI Gateway invites — outside the admin
// layout because devs landing here are not (and need not be) logged in.
const ClaimGatewayKey = lazy(() => import('../pages/claim/ClaimGatewayKey'));
// `saf3ai auth login --no-browser` device-code approval — requires an existing
// session (ProtectedRoute below), unlike the claim page above.
const DeviceCodeVerify = lazy(() => import('../pages/device'));
// `saf3ai auth login` browser sign-in approval (the CLI's loopback flow).
// Session-gated like /device; ProtectedRoute keeps the request's query string
// through sign-in.
const CliAuthorize = lazy(() => import('../pages/oauth-authorize'));
const Welcome = lazy(() => import('../pages/welcome'));
const Dashboard = lazy(() => import('../pages/dashboard'));
const ConversationExplorer = lazy(() => import('../pages/conversation-explorer'));
const CustomAgentsIntentAnalysis = lazy(() => import('../pages/intent-analysis'));

// v2 platform — wrappers + landings. All under /v2/* so v1 routes are
// untouched; the v1 pages they wrap are imported by name and rendered as
// children with NO modifications.
const WorkflowPageWrapper = lazy(() => import('../pages/v2/_shared/WorkflowPageWrapper'));
const V2ApplicationsLanding = lazy(() => import('../pages/v2/discovery/applications'));
const V2SourcesLanding = lazy(() => import('../pages/v2/discovery/sources'));
const V2RegistryLanding = lazy(() => import('../pages/v2/discovery/registry'));
const V2ModelsLanding = lazy(() => import('../pages/v2/discovery/models'));
const V2KnowledgeGapsLanding = lazy(() => import('../pages/v2/insights/knowledge-gaps'));
const V2IntentLanding = lazy(() => import('../pages/v2/insights/intent'));
const V2AccessLanding = lazy(() => import('../pages/v2/posture/access'));
const V2VendorSettingsLanding = lazy(() => import('../pages/v2/admin/settings'));
const AgentNetwork = lazy(() => import('../pages/agent-network'));
const ModelEvaluation = lazy(() => import('../pages/model-evaluation'));
const CloneEvaluation = lazy(() => import('../pages/model-evaluation/clone'));
const EvaluationProjects = lazy(() => import('../pages/model-evaluation-projects'));
const EvaluationArena = lazy(() => import('../pages/model-evaluation-arena'));
const ROIAnalysis = lazy(() => import('../pages/roi-analysis'));
const ManageUsers = lazy(() => import('../pages/admin/manage-users'));
const ManageAgents = lazy(() => import('../pages/admin/manage-agents'));
const ManageTeams = lazy(() => import('../pages/admin/manage-teams'));
const CustomRoles = lazy(() => import('../pages/admin/custom-roles'));
const CustomRoleEditor = lazy(() => import('../pages/admin/custom-roles/editor'));
const RecommendationRules = lazy(() => import('../pages/admin/recommendation-rules'));
const CustomGuardrails = lazy(() => import('../pages/admin/custom-guardrails'));
const AlertConfiguration = lazy(() => import('../pages/admin/alert-configuration'));
const AddSubscription = lazy(() => import('../pages/admin/alert-configuration/AddSubscription'));
const ComplianceDashboard = lazy(() => import('../pages/compliance/compliance-dashboard'));
const CustomAgentsAuditReport = lazy(() => import('../pages/compliance/audit-report'));
const CustomAgentsCompliance = lazy(() => import('../pages/custom-agents/CustomAgentsCompliance'));
const CustomAgentsUsageRoi = lazy(() => import('../pages/custom-agents/CustomAgentsUsageRoi'));
const CustomAgentsConversations = lazy(() => import('../pages/custom-agents/CustomAgentsConversations'));
const CustomAgentsKnowledgeGaps = lazy(() => import('../pages/custom-agents/CustomAgentsKnowledgeGaps'));
const CustomAgentsUsers = lazy(() => import('../pages/custom-agents/CustomAgentsUsers'));
const SecurityDashboard = lazy(() => import('../pages/security/security-dashboard'));
const UserActivity = lazy(() => import('../pages/user-activity'));
const Billing = lazy(() => import('../pages/billing'));
const GlobalSearch = lazy(() => import('../pages/global-search'));
const AIExplorer = lazy(() => import('../pages/ai-explorer'));
const AIDashboards = lazy(() => import('../pages/ai-explorer/dashboards'));
const AIDashboardViewer = lazy(() => import('../pages/ai-explorer/dashboards/viewer'));
const RedTeamScanner = lazy(() => import('../pages/red-team-scanner'));
const ScheduledScansView = lazy(() => import('../pages/red-team-scanner/components/ScheduledScans/ScheduledScansView'));
const Profile = lazy(() => import('../pages/profile'));
const SSOCallback = lazy(() => import('../pages/sso-callback'));
const ImpersonationHandoff = lazy(() => import('../pages/impersonation-handoff'));
const SSOSettings = lazy(() => import('../pages/admin/sso-settings'));
const ApiKeys = lazy(() => import('../pages/admin/api-keys'));
const EndpointSettings = lazy(() => import('../pages/admin/endpoint-settings'));
const EndpointViolations = lazy(() => import('../pages/admin/endpoint-violations'));
const FleetOverview = lazy(() => import('../pages/admin/fleet-overview'));
const ShadowAIDiscovery = lazy(() => import('../pages/admin/shadow-ai-discovery'));
// Shadow AI (consolidated) — the browser extension, MDM, EDR, the inline
// gateway and the SWG feed as one module — the `endpoint` licence (MDM, EDR
// and the AI Gateway pages are their own modules: mdm, edr, ai_gateway).
const ShadowAiOverview = lazy(() => import('../pages/shadow-ai/overview'));
const ShadowAiLogExplorer = lazy(() => import('../pages/shadow-ai/log-explorer'));
const ShadowAiToolCatalog = lazy(() => import('../pages/shadow-ai/tool-catalog'));
const ShadowAiUsers = lazy(() => import('../pages/shadow-ai/users'));
const ShadowAiEndpoints = lazy(() => import('../pages/shadow-ai/endpoints'));
const ShadowAiIdentityDetail = lazy(() => import('../pages/shadow-ai/identity-detail'));
const ShadowAiEndpointDetail = lazy(() => import('../pages/shadow-ai/endpoint-detail'));
const ShadowAiDlpAnalytics = lazy(() => import('../pages/shadow-ai/dlp-analytics'));
const ShadowAiGuardrails = lazy(() => import('../pages/shadow-ai/guardrails'));
const ShadowAiCoverage = lazy(() => import('../pages/shadow-ai/coverage'));
const ShadowAiAlerts = lazy(() => import('../pages/shadow-ai/alerts'));
// Endpoint Security pages as pinned Atlas pages (Phase 4). Previewed at
// /endpoint-security/preview/*; they replace the module's own page only once
// signed off in pages/shadow-ai/atlas/switch.js.
const EndpointAtlasAlerts = lazy(() => import('../pages/shadow-ai/atlas').then((m) => ({ default: m.EndpointAlerts })));
const EndpointAtlasPolicy = lazy(() => import('../pages/shadow-ai/atlas').then((m) => ({ default: m.EndpointPolicy })));
// Endpoint Compliance on the Atlas theme (GE's look): the module page with its Atlas header opt-in.
const EndpointAtlasCompliance = lazy(() => import('../pages/shadow-ai/atlas/EndpointCompliance'));
const EndpointAuditReport = lazy(() => import('../pages/shadow-ai/compliance').then((m) => ({ default: m.EndpointAuditReport })));
const AIGatewayOverview = lazy(() => import('../pages/admin/ai-gateway/Overview'));
const AIGatewayLiveEvents = lazy(() => import('../pages/admin/ai-gateway/LiveEvents'));
const AIGatewayConfiguration = lazy(() => import('../pages/admin/ai-gateway/Configuration'));
const AIGatewayKeys = lazy(() => import('../pages/admin/ai-gateway/Keys'));
const AIGatewayUsage = lazy(() => import('../pages/admin/ai-gateway/Usage'));
const AIGatewayPolicy = lazy(() => import('../pages/admin/ai-gateway/Policy'));
// Managed Devices (Jamf / Intune) — the `mdm` module.
const MdmOverview = lazy(() => import('../pages/admin/mdm/Overview'));
const MdmDevices = lazy(() => import('../pages/admin/mdm/Devices'));
const MdmSoftware = lazy(() => import('../pages/admin/mdm/Software'));
const MdmMcp = lazy(() => import('../pages/admin/mdm/Mcp'));
const MdmPolicy = lazy(() => import('../pages/admin/mdm/Policy'));
// EDR (CrowdStrike + Defender) — the `edr` module.
const EdrOverview = lazy(() => import('../pages/admin/edr/Overview'));
const EdrEndpoints = lazy(() => import('../pages/admin/edr/Endpoints'));
const EdrProcesses = lazy(() => import('../pages/admin/edr/Processes'));
const EdrExtensions = lazy(() => import('../pages/admin/edr/Extensions'));
const EdrMcp = lazy(() => import('../pages/admin/edr/Mcp'));
const EdrAlerts = lazy(() => import('../pages/admin/edr/Alerts'));
const DLPAnalytics = lazy(() => import('../pages/admin/dlp-analytics'));
const EndpointUserActivity = lazy(() => import('../pages/admin/endpoint-user-activity'));
const AIToolCatalog = lazy(() => import('../pages/admin/ai-tool-catalog'));
const AIConversations = lazy(() => import('../pages/admin/ai-conversations'));
const AgentBehaviorProfile = lazy(() => import('../pages/admin/policy-manager/pages/AgentBehaviorProfile'));
const AdminAuditLog = lazy(() => import('../pages/admin/audit-log'));
const AdminEndpointAlerts = lazy(() => import('../pages/admin/endpoint-alerts'));
const DataPolicy = lazy(() => import('../pages/admin/data-policy'));
const AiProvider = lazy(() => import('../pages/admin/ai-provider'));
const IntegrationsSettings = lazy(() => import('../pages/admin/integrations'));

// Agent Atlas Pages — enterprise-wide unified agent view
const AgentAtlas = lazy(() => import('../pages/agent-atlas/atlas'));
const AgentAtlasInventory = lazy(() => import('../pages/agent-atlas/inventory'));
const AgentAtlasUsers = lazy(() => import('../pages/agent-atlas/users'));
const AgentAtlasCompliance = lazy(() => import('../pages/agent-atlas/compliance'));
const AgentAtlasPolicy = lazy(() => import('../pages/agent-atlas/policy'));
const AgentAtlasComplianceAudit = lazy(() => import('../pages/agent-atlas/compliance-audit'));

// Security (Atlas) — consolidated AI-SOC / AI-SPM module
const SAOverview = lazy(() => import('../pages/security-atlas/overview'));
const SAIncidents = lazy(() => import('../pages/security-atlas/incidents'));
const SAIncidentDetail = lazy(() => import('../pages/security-atlas/incidents/detail'));
const SASignals = lazy(() => import('../pages/security-atlas/signals'));
const SAResponse = lazy(() => import('../pages/security-atlas/response'));
const SAAutomation = lazy(() => import('../pages/security-atlas/automation'));
const SAAiBom = lazy(() => import('../pages/security-atlas/ai-bom'));
const SADataSecurity = lazy(() => import('../pages/security-atlas/data-security'));
const SAMcpSecurity = lazy(() => import('../pages/security-atlas/mcp-security'));
const SAAgentInstructions = lazy(() => import('../pages/security-atlas/agent-instructions'));
const SAExceptions = lazy(() => import('../pages/security-atlas/exceptions'));
const SATuning = lazy(() => import('../pages/security-atlas/tuning'));
const SACoverageDrift = lazy(() => import('../pages/security-atlas/coverage-drift'));
const SADetectionHealth = lazy(() => import('../pages/security-atlas/detection-health'));
const SAHunt = lazy(() => import('../pages/security-atlas/hunt'));
const SASkills = lazy(() => import('../pages/security-atlas/skills'));
const SADeepfakeDetector = lazy(() => import('../pages/security-atlas/deepfake-detector'));
const AgentAtlasRoi = lazy(() => import('../pages/agent-atlas/finops/roi'));
const AgentAtlasLicensing = lazy(() => import('../pages/agent-atlas/finops/licensing'));
const AgentAtlasBudgets = lazy(() => import('../pages/agent-atlas/finops/budgets'));
const AgentAtlasFinOpsSettings = lazy(() => import('../pages/agent-atlas/finops/settings'));
const AgentAtlasAlerts = lazy(() => import('../pages/agent-atlas/alerts'));
const AgentAtlasLogExplorer = lazy(() => import('../pages/agent-atlas/log-explorer'));
// Adoption: conversations, knowledge gaps and intent merged across every classified surface.
const AgentAtlasConversations = lazy(() => import('../pages/agent-atlas/adoption/conversations'));
const AgentAtlasKnowledgeGaps = lazy(() => import('../pages/agent-atlas/adoption/knowledge-gaps'));
const AgentAtlasIntentAnalysis = lazy(() => import('../pages/agent-atlas/adoption/intent'));
const AgentAtlasSources = lazy(() => import('../pages/agent-atlas/adoption/AtlasSources'));
// /agent-atlas/assessment: the overview, or Findings under the "New navigation" switch.
const AssessmentOverview = lazy(() => import('../pages/agent-atlas/assessment/overview'));
const AssessmentRedTeam = lazy(() => import('../pages/agent-atlas/assessment/red-team'));
const AssessmentSchedules = lazy(() => import('../pages/agent-atlas/assessment/schedules'));
const AssessmentEvaluations = lazy(() => import('../pages/agent-atlas/assessment/evaluations'));
const AssessmentScanReport = lazy(() => import('../pages/agent-atlas/assessment/report'));
const AssessmentEvaluationReport = lazy(() => import('../pages/agent-atlas/assessment/evaluation'));
const AssessmentScanPrompts = lazy(() => import('../pages/agent-atlas/assessment/report/prompts'));
const AgentAtlasAgentDetail = lazy(() => import('../pages/agent-atlas/agent-detail'));
const AgentAtlasUserDetail = lazy(() => import('../pages/agent-atlas/user-detail'));
const AgentAtlasCoverage = lazy(() => import('../pages/agent-atlas/coverage'));
const AgentAtlasInbox = lazy(() => import('../pages/agent-atlas/recommendations'));

// Cloud — cross-cloud AI discovery (AWS / GCP / Azure audit logs).
// Two-layer surface: control-plane (Inventory / Principals — from
// metadata audit logs) and data-plane (Models / Agents / Knowledge
// Bases / Security Events / Topology — from the aws_bedrock /
// vertex_ai / azure_openai content collectors).
const CloudOverview = lazy(() => import('../pages/cloud/overview'));
const CloudInventory = lazy(() => import('../pages/cloud/inventory'));
const CloudPrincipals = lazy(() => import('../pages/cloud/principals'));
const CloudModels = lazy(() => import('../pages/cloud/models'));
const CloudAgents = lazy(() => import('../pages/cloud/agents'));
const CloudKnowledgeBases = lazy(() => import('../pages/cloud/knowledge-bases'));
const CloudPrompts = lazy(() => import('../pages/cloud/prompts'));
const CloudSecurityEvents = lazy(() => import('../pages/cloud/security-events'));
const CloudChanges = lazy(() => import('../pages/cloud/changes'));
const CloudCompliance = lazy(() => import('../pages/cloud/compliance'));
const CloudAuditLog = lazy(() => import('../pages/cloud/audit-log'));
const CloudPosture = lazy(() => import('../pages/cloud/posture'));
const CloudTopology = lazy(() => import('../pages/cloud/topology'));

// Preserve deep links to the former standalone AI Workloads page while
// routing every workload use case into the consolidated inventory.
const LegacyCloudPrincipalsRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/cloud/identities', search: location.search }} replace />;
};

const LegacyCloudWorkloadsRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/cloud/inventory', search: location.search }} replace />;
};

// M365 Copilot Pages (nested under CopilotLayout — mirrors Gemini Agents nav)
const CopilotDashboard = lazy(() => import('../pages/copilot/dashboard'));
const CopilotUsers = lazy(() => import('../pages/copilot/users'));
const CopilotKnowledgeGaps = lazy(() => import('../pages/copilot/knowledge-gaps'));
const CopilotIntentAnalysis = lazy(() => import('../pages/copilot/intent-analysis'));
const CopilotConversations = lazy(() => import('../pages/copilot/conversations'));
const CopilotPosture = lazy(() => import('../pages/copilot/posture'));
const CopilotSources = lazy(() => import('../pages/copilot/sources'));
const CopilotRegistry = lazy(() => import('../pages/copilot/registry'));
const CopilotAuditLog = lazy(() => import('../pages/copilot/audit-log'));
const CopilotCompliance = lazy(() => import('../pages/copilot/compliance'));
const CopilotAuditReport = lazy(() => import('../pages/copilot/audit-report'));
const CopilotAlerts = lazy(() => import('../pages/copilot/alerts'));
const CopilotSettings = lazy(() => import('../pages/copilot/settings'));

// Gemini Pages (Enterprise AI - Google Workspace)
const GeminiDashboard = lazy(() => import('../pages/gemini/dashboard'));
const GeminiInteractions = lazy(() => import('../pages/gemini/interactions'));
const GeminiUsers = lazy(() => import('../pages/gemini/users'));
const GeminiApplications = lazy(() => import('../pages/gemini/applications'));
const GeminiIntentAnalysis = lazy(() => import('../pages/gemini/intent-analysis'));
const GeminiUsageROI = lazy(() => import('../pages/gemini/usage-roi'));
const GeminiAuditLog = lazy(() => import('../pages/gemini/audit-log'));
const GeminiCompliance = lazy(() => import('../pages/gemini/compliance'));
const GeminiAuditReport = lazy(() => import('../pages/gemini/audit-report'));
const GeminiAlerts = lazy(() => import('../pages/gemini/alerts'));
const GeminiSettings = lazy(() => import('../pages/gemini/settings'));
const GeminiKnowledgeGaps = lazy(() => import('../pages/gemini/knowledge-gaps'));
// Gemini Enterprise Agents Pages
const GeminiAgentsPosture = lazy(() => import('../pages/gemini/agents/posture'));
const GeminiAgentsPolicyViolations = lazy(() => import('../pages/gemini/agents/violations'));
// Gemini Enterprise on Atlas: GE-only wrappers that mount Atlas pages (or a GE
// page under an Atlas header). The GE page files above stay — Copilot, Copilot
// Studio and Gemini Workspace still mount them.
const GeminiEnterpriseDashboard = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseDashboard'));
const GeminiEnterpriseAuditLog = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseAuditLog'));
const GeminiEnterpriseUsageRoi = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseUsageRoi'));
const GeminiEnterpriseAlerts = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseAlerts'));
const GeminiEnterpriseCompliance = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseCompliance'));
const GeminiEnterpriseAuditReport = lazy(() => import('../pages/gemini/agents/audit-report'));
const GeminiEnterprisePolicy = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterprisePolicy'));
const GeminiEnterpriseSources = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseSources'));
const GeminiEnterpriseAccess = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseAccess'));
const GeminiEnterpriseSettings = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseSettings'));
const GeminiEnterpriseRegistry = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseRegistry'));
const GeminiEnterpriseUsers = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseUsers'));
const GeminiEnterpriseConversations = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseConversations'));
const GeminiEnterpriseKnowledgeGaps = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseKnowledgeGaps'));
const GeminiEnterpriseIntentAnalysis = lazy(() => import('../pages/gemini/agents/atlas/GeminiEnterpriseIntentAnalysis'));
// Copilot Studio Pages (re-export Gemini Agents pages via CopilotStudioLayout context)
const CopilotStudioLayout = lazy(() => import('../pages/copilot/studio/CopilotStudioLayout'));
const CopilotStudioDashboard = lazy(() => import('../pages/copilot/studio/dashboard'));
const CopilotStudioConversations = lazy(() => import('../pages/copilot/studio/conversations'));
const CopilotStudioPosture = lazy(() => import('../pages/copilot/studio/posture'));
const CopilotStudioIntentAnalysis = lazy(() => import('../pages/copilot/studio/intent-analysis'));
const CopilotStudioKnowledgeGaps = lazy(() => import('../pages/copilot/studio/knowledge-gaps'));
const CopilotStudioSources = lazy(() => import('../pages/copilot/studio/sources'));
const CopilotStudioRegistry = lazy(() => import('../pages/copilot/studio/registry'));
const CopilotStudioAuditLog = lazy(() => import('../pages/copilot/studio/audit-log'));
const CopilotStudioUsers = lazy(() => import('../pages/copilot/studio/users'));
const CopilotStudioCompliance = lazy(() => import('../pages/copilot/studio/compliance'));
const CopilotStudioAuditReport = lazy(() => import('../pages/copilot/studio/audit-report'));
const CopilotStudioAlerts = lazy(() => import('../pages/copilot/studio/alerts'));
const CopilotStudioSettings = lazy(() => import('../pages/copilot/studio/settings'));

// AI Providers (/saas-ai): every page drawn by Atlas, pinned to the provider picked in the
// module toolbar (src/pages/saas-ai/atlas, 2026-10-10 — the Gemini Enterprise pattern).
const AiProvidersDashboard = lazy(() => import('../pages/saas-ai/atlas/AiProvidersDashboard'));
const AiProvidersRegistry = lazy(() => import('../pages/saas-ai/atlas/AiProvidersRegistry'));
const AiProvidersConversations = lazy(() => import('../pages/saas-ai/atlas/AiProvidersConversations'));
const AiProvidersAlerts = lazy(() => import('../pages/saas-ai/atlas/AiProvidersAlerts'));
const AiProvidersUsers = lazy(() => import('../pages/saas-ai/atlas/AiProvidersUsers'));
const AiProvidersIntentAnalysis = lazy(() => import('../pages/saas-ai/atlas/AiProvidersIntentAnalysis'));
const AiProvidersKnowledgeGaps = lazy(() => import('../pages/saas-ai/atlas/AiProvidersKnowledgeGaps'));
const AiProvidersCompliance = lazy(() => import('../pages/saas-ai/atlas/AiProvidersCompliance'));
const AiProvidersAuditReport = lazy(() => import('../pages/saas-ai/atlas/AiProvidersCompliance').then((m) => ({ default: m.AiProvidersAuditReport })));
const AiProvidersAuditLog = lazy(() => import('../pages/saas-ai/atlas/AiProvidersAuditLog'));
const AiProvidersSettings = lazy(() => import('../pages/saas-ai/atlas/AiProvidersSettings'));
const SaasRecommendations = lazy(() => import('../pages/saas-ai/recommendations'));
const SaasAgentNetwork = lazy(() => import('../pages/saas-ai/agent-network'));
// A module's FinOps section: Atlas Tokenomics and Budgets & Alerts pinned to its surface,
// beside the module's own Usage & ROI page where it still has one (?tab=).
const ModuleFinOpsTabs = lazy(() => import('../pages/agent-atlas/finops/ModuleFinOpsTabs'));
const SaasFinOpsTabs = lazy(() => import('../pages/agent-atlas/finops/ModuleFinOpsTabs').then((m) => ({ default: m.SaasFinOpsTabs })));
const AgentAtlasTokenomics = lazy(() => import('../pages/agent-atlas/finops/tokenomics'));
const SaasAiPolicy = lazy(() => import('../pages/saas-ai/policy'));

// SAST (Code Security) Pages
const SASTOverview = lazy(() => import('../pages/sast/overview'));
const SASTRepositories = lazy(() => import('../pages/sast/repositories'));
const SASTScanner = lazy(() => import('../pages/sast/scanner'));
const SASTSettings = lazy(() => import('../pages/sast/settings'));
const SASTPolicy = lazy(() => import('../pages/sast/policy'));
const SASTCompliance = lazy(() => import('../pages/sast/compliance'));
const SASTFindings = lazy(() => import('../pages/sast/dashboard/findings'));
const SASTRecommendations = lazy(() => import('../pages/sast/recommendations'));
const SASTGitHubCallback = lazy(() => import('../pages/sast/github-callback'));

// Security Pages
const ThreatControlCenter = lazy(() => import('../pages/security/threat-control-center'));
const ThreatIntelligence = lazy(() => import('../pages/security/threat-intelligence'));
const AttackPath = lazy(() => import('../pages/security/attack-path'));
const IncidentResponse = lazy(() => import('../pages/security/incident-response'));
const SecurityPosture = lazy(() => import('../pages/security/security-posture'));
const ComplianceEvidence = lazy(() => import('../pages/security/compliance-evidence'));

const ObservabilityPage = lazy(() => import('../pages/observability/ObservabilityPage'));
const CustomAgentsAlerts = lazy(() => import('../pages/custom-agents/CustomAgentsAlerts'));
const CustomAgentsRecommendations = lazy(() => import('../pages/custom-agents/CustomAgentsRecommendations'));
const CustomAgentsPolicy = lazy(() => import('../pages/custom-agents/CustomAgentsPolicy'));
const CustomAgentsAuditLog = lazy(() => import('../pages/custom-agents/CustomAgentsAuditLog'));
const CustomAgentsSettings = lazy(() => import('../pages/custom-agents/CustomAgentsSettings'));
// Source Intelligence in the Atlas style, for the custom surface (no Atlas page to inherit).
const CustomAgentsSources = lazy(() => import('../pages/custom-agents/CustomAgentsSources'));
const CustomAgentsRegistry = lazy(() => import('../pages/custom-agents/CustomAgentsRegistry'));
const CustomAgentsDashboard = lazy(() => import('../pages/custom-agents/CustomAgentsDashboard'));
// Custom Agents → Assessment: the Atlas Assessment pages pinned to Custom Agents.
const CustomAgentsAssessmentOverview = lazy(() => import('../pages/custom-agents/CustomAgentsAssessment').then((m) => ({ default: m.CustomAgentsAssessmentOverview })));
const CustomAgentsRedTeam = lazy(() => import('../pages/custom-agents/CustomAgentsAssessment').then((m) => ({ default: m.CustomAgentsRedTeam })));
const CustomAgentsSchedules = lazy(() => import('../pages/custom-agents/CustomAgentsAssessment').then((m) => ({ default: m.CustomAgentsSchedules })));
const CustomAgentsEvaluations = lazy(() => import('../pages/custom-agents/CustomAgentsAssessment').then((m) => ({ default: m.CustomAgentsEvaluations })));

// Full page loading fallback (for routes without AppLayout like login, landing)
const FullPageLoadingFallback = () => <LoadingSpinner fullPage message="Loading page..." />;

/**
 * Custom Agents' AI Explorer and the legacy /dashboard. Red-team audits, scheduled scans and
 * model evaluation are the `redteam` module; Atlas and Policy pages are core
 * (never locked). An unlicensed module's pages are not hidden: ProtectedRoute
 * renders the lock screen in their place.
 */
const GOVERNANCE_OR_EVAL_MODULES = ['monitoring', 'governance'];

/**
 * Forwards /shadow-ai/... to /endpoint-security/..., keeping everything after
 * the prefix — the sub-path, the query string and the hash. A Log Explorer
 * link carrying ?person=…&q=… has to survive the move, or the redirect is
 * worse than a broken link: it looks like it worked and silently drops the
 * filter the reader was looking at.
 */
const RedirectShadowAi = () => {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/shadow-ai/, '');
  return (
    <Navigate
      to={`/endpoint-security${rest}${location.search}${location.hash}`}
      replace
    />
  );
};

/** Redirects to `to`, preserving the current query string (e.g. ?alert_id=123). */
const RedirectPreserveQuery = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

/** Responsible AI (retired): Compliance's AI systems panel, opened on `?system=` when one was linked. */
const ResponsibleAiRedirect = () => {
  const system = new URLSearchParams(useLocation().search).get('system');
  const search = new URLSearchParams({ panel: 'systems', ...(system ? { ssys: system } : {}) });
  return <Navigate to={`/agent-atlas/compliance?${search}`} replace />;
};

/** Replace-redirect that keeps the query string and hash (see buildRedirectTarget). */
const RedirectPreserveLocation = ({ to }) => {
  const location = useLocation();
  return <Navigate to={buildRedirectTarget(to, location)} replace />;
};

/**
 * /dashboard no longer renders the legacy Custom Agents page: it forwards to
 * the Custom Agents Dashboard (/custom-agents/dashboard). Tenants without the
 * monitoring module cannot open that page, so they keep the legacy page until
 * it has a replacement. CUSTOM_AGENTS_DASHBOARD is the rollback switch.
 *
 * No loop: /custom-agents/dashboard is its own top-level route (below), so it
 * never reaches CustomAgentsRoutesGate, whose flag-off fallback is /dashboard.
 */
const LegacyDashboardRoute = () => {
  // Canonical licence check (aliases such as log_tracer count as monitoring).
  const canOpenDashboard = getModuleLicense().isLicensed('monitoring');
  if (isFeatureEnabled('CUSTOM_AGENTS_DASHBOARD') && canOpenDashboard) {
    return <RedirectPreserveLocation to={CUSTOM_AGENTS_DASHBOARD_PATH} />;
  }
  return (
    <AppLayout>
      <Suspense fallback={<PageLoadingFallback message="Loading dashboard..." />}>
        <Dashboard />
      </Suspense>
    </AppLayout>
  );
};

/** Security Atlas pages Custom Agents mounts beyond the common module set, so the
 * pinned Security Overview drills into them rather than a stand-in. */
const CUSTOM_AGENTS_SECURITY_EXTRA_PAGES = ['data-security', 'ai-bom'];

/**
 * Entry to the namespaced /custom-agents routes. While CUSTOM_AGENTS_ROUTES is
 * off, a canonical URL forwards to the legacy page it mirrors with its query
 * and hash intact, so a link shared ahead of the rollout still opens the page.
 */
const CustomAgentsRoutesGate = () => {
  const location = useLocation();
  if (!isFeatureEnabled('CUSTOM_AGENTS_ROUTES')) {
    return <RedirectPreserveLocation to={fallbackPathFor(location.pathname) ?? '/dashboard'} />;
  }
  return (
    <AppLayout>
      <CustomAgentsLayout />
    </AppLayout>
  );
};

/**
 * The standalone Custom Agents assessment tools (Red Team Audit, Scheduled Scans, Model
 * Evaluation) now render the Atlas Assessment pages pinned to Custom Agents, under
 * /custom-agents/assessment. Their old URLs forward there with query and hash intact. While
 * CUSTOM_AGENTS_ROUTES is off the old page renders instead — no loop: the gate's fallback for
 * those canonical URLs is this legacy URL.
 */
const LegacyAssessmentRoute = ({ to, children }) => {
  const params = useParams();
  const location = useLocation();
  if (!isFeatureEnabled('CUSTOM_AGENTS_ROUTES')) return children;
  return <RedirectPreserveLocation to={typeof to === 'function' ? to(params, location) : to} />;
};
const CA_ASSESSMENT = '/custom-agents/assessment';
// /red-team-scanner?view=create|detail|prompts-detail is the Red Team tool's own screen; without
// a view it is the scan list.
const redTeamScannerTarget = (_, location) => (
  new URLSearchParams(location.search).get('view') ? `${CA_ASSESSMENT}/red-team/scan` : `${CA_ASSESSMENT}/red-team`
);

const AppRoutes = () => {
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    const handler = () => setSessionKey((k) => k + 1);
    window.addEventListener(SESSION_CONTINUED_EVENT, handler);
    return () => window.removeEventListener(SESSION_CONTINUED_EVENT, handler);
  }, []);

  return (
    <CustomAgentsTimeRange key={sessionKey}>
    <Routes>
      <Route
        path="/"
        element={
          <Suspense fallback={<FullPageLoadingFallback />}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/sso/callback"
        element={
          <Suspense fallback={<FullPageLoadingFallback />}>
            <SSOCallback />
          </Suspense>
        }
      />
      <Route
        path="/impersonation/handoff"
        element={
          <Suspense fallback={<FullPageLoadingFallback />}>
            <ImpersonationHandoff />
          </Suspense>
        }
      />
      <Route
        path="/claim/:token"
        element={
          <Suspense fallback={<FullPageLoadingFallback />}>
            <ClaimGatewayKey />
          </Suspense>
        }
      />
      <Route
        path="/device"
        element={
          <ProtectedRoute>
            <Suspense fallback={<FullPageLoadingFallback />}>
              <DeviceCodeVerify />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oauth/authorize"
        element={
          <ProtectedRoute>
            <Suspense fallback={<FullPageLoadingFallback />}>
              <CliAuthorize />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading welcome..." />}>
                <Welcome />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredAnyModule={GOVERNANCE_OR_EVAL_MODULES}>
            <LegacyDashboardRoute />
          </ProtectedRoute>
        }
      />
      {/* The AI Ops Dashboard is retired (2026-10-17): nothing linked to it, it showed 30-day
          numbers whatever range was picked, and Atlas shows the same per-surface figures from the
          engine. Old links land on Atlas. */}
      <Route path="/enterprise-ai/dashboard" element={<Navigate to="/agent-atlas/atlas" replace />} />
      {/* Action Center - Hidden */}
      {/* <Route
        path="/action-center"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Action Center..." />}>
                <ActionCenterPage />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/intent-analysis"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}>
                <CustomAgentsIntentAnalysis />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/observability"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading observability..." />}>
                <ObservabilityPage />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* The Custom Agents Dashboard — the module overview. Its own route, ahead
          of the gated /custom-agents block, so it renders whatever the
          CUSTOM_AGENTS_ROUTES flag says and /dashboard can always forward here
          without bouncing back. Same providers as the gated pages. */}
      <Route
        path="/custom-agents/dashboard"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <CustomAgentsLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading dashboard..." />}>
                  <CustomAgentsDashboard />
                </Suspense>
              </CustomAgentsLayout>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Custom Agents — canonical namespaced routes (transition report §11).
          Each child keeps its legacy route's module gate, and the legacy routes
          stay primary: nothing in the nav links here until the navigation
          release. CustomAgentsRoutesGate forwards to the legacy page while
          CUSTOM_AGENTS_ROUTES is off. */}
      <Route
        path="/custom-agents"
        element={
          <ProtectedRoute>
            <CustomAgentsRoutesGate />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="agent-network" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback />}><AgentNetwork lockedSurface="custom" /></Suspense></ProtectedRoute>} />
        <Route path="registry" element={<ForwardEvaluationsTab><ProtectedRoute><Suspense fallback={<PageLoadingFallback message="Loading Agent Registry..." />}><CustomAgentsRegistry /></Suspense></ProtectedRoute></ForwardEvaluationsTab>} />
        <Route path="users" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Users..." />}><CustomAgentsUsers /></Suspense></ProtectedRoute>} />
        {/* Agent 360 and User 360, mounted in the module (as Gemini Enterprise does) so a registry
            or users row stays inside /custom-agents, pinned to Custom Agents: the security plane's
            `otel`, Atlas's `custom`. */}
        <Route element={<SurfaceScope surface={CUSTOM_AGENTS_MODULE.securitySurface} atlasSurface={CUSTOM_AGENTS_MODULE.graphSurface} />}>
          <Route path="registry/:agentId" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading agent..." />}><AgentAtlasAgentDetail /></Suspense></ProtectedRoute>} />
          <Route path="users/:email" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading user..." />}><AgentAtlasUserDetail /></Suspense></ProtectedRoute>} />
        </Route>
        {/* FinOps is the Gemini Enterprise Usage & ROI page (Usage & ROI | Tokenomics);
            the old Usage & ROI address forwards to it with its query. */}
        <Route path="usage-roi" element={<RedirectPreserveLocation to="/custom-agents/finops" />} />
        <Route path="finops" element={<ProtectedRoute requiredModule="finops"><Suspense fallback={<PageLoadingFallback message="Loading FinOps..." />}><CustomAgentsUsageRoi /></Suspense></ProtectedRoute>} />
        <Route path="conversations" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}><CustomAgentsConversations /></Suspense></ProtectedRoute>} />
        <Route path="knowledge-gaps" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Knowledge Gaps..." />}><CustomAgentsKnowledgeGaps /></Suspense></ProtectedRoute>} />
        <Route path="intent-analysis" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}><CustomAgentsIntentAnalysis /></Suspense></ProtectedRoute>} />
        <Route path="alerts" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}><CustomAgentsAlerts /></Suspense></ProtectedRoute>} />
        {/* The Atlas Inbox pinned to the engine's `custom_agents` module. Gated
            on `monitoring` like the module's other telemetry pages: the rules
            read observability spans, so the entitlement that grants those is
            the one that makes this page meaningful. */}
        <Route path="recommendations" element={<ProtectedRoute requiredAnyModule={['monitoring', 'security']}><Suspense fallback={<PageLoadingFallback message="Loading Recommendations..." />}><CustomAgentsRecommendations /></Suspense></ProtectedRoute>} />
        {/* Threats Control left Custom Agents; its canonical URL lands on the module's Security Overview. */}
        <Route path="security/threats-control" element={<Navigate to="/custom-agents/security/overview" replace />} />
        {/* Gemini-style security operations, backed by the shared Atlas pages
            and narrowed to Custom Agents (the `otel` security surface). These
            remain distinct from Attack Path, Logs Explorer and Red Team Audit. */}
        <Route element={<SurfaceScope surface={CUSTOM_AGENTS_MODULE.securitySurface} emptyOnUnavailable />}>
          <Route path="sources" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Source Intelligence..." />}><CustomAgentsSources /></Suspense></ProtectedRoute>} />
          <Route path="security/signals" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}><SASignals /></Suspense></ProtectedRoute>} />
          <Route path="security/hunt" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}><SAHunt /></Suspense></ProtectedRoute>} />
          <Route path="security/incidents" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}><SAIncidents /></Suspense></ProtectedRoute>} />
          <Route path="security/incidents/:id" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}><SAIncidentDetail /></Suspense></ProtectedRoute>} />
          <Route path="security/skills" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}><SASkills /></Suspense></ProtectedRoute>} />
          <Route path="security/response" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Response..." />}><SAResponse /></Suspense></ProtectedRoute>} />
          <Route path="security/exceptions" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}><SAExceptions /></Suspense></ProtectedRoute>} />
          {/* Security Overview, Data Security and AI-BOM: the Security Atlas pages themselves,
              pinned to otel. Each hides what the backend can only answer estate-wide. */}
          <Route path="security/overview" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Security Overview..." />}><SAOverview modulePages={CUSTOM_AGENTS_SECURITY_EXTRA_PAGES} /></Suspense></ProtectedRoute>} />
          <Route path="security/data-security" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Data Security..." />}><SADataSecurity traceDetail /></Suspense></ProtectedRoute>} />
          <Route path="security/ai-bom" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}><SAAiBom /></Suspense></ProtectedRoute>} />
          <Route path="posture" element={<ProtectedRoute requiredModule="monitoring"><Suspense fallback={<PageLoadingFallback message="Loading Access Management..." />}><GeminiAgentsPosture /></Suspense></ProtectedRoute>} />
        </Route>
        <Route path="compliance" element={<ProtectedRoute requiredModule="governance"><Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}><CustomAgentsCompliance /></Suspense></ProtectedRoute>} />
        <Route path="audit-log" element={<ProtectedRoute><Suspense fallback={<PageLoadingFallback message="Loading Audit Log..." />}><CustomAgentsAuditLog /></Suspense></ProtectedRoute>} />
        <Route path="policy" element={<ProtectedRoute><Suspense fallback={<PageLoadingFallback message="Loading Policy..." />}><CustomAgentsPolicy /></Suspense></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute><Suspense fallback={<PageLoadingFallback message="Loading Settings..." />}><CustomAgentsSettings /></Suspense></ProtectedRoute>} />
        {/* Assessment: the Atlas Assessment pages pinned to Custom Agents (CustomAgentsAssessment),
            with the Red Team / Model Evaluation tool screens and the Atlas reports under the same
            prefix, so following a scan or an evaluation never leaves the module. */}
        <Route path="assessment" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading Assessment..." />}><CustomAgentsAssessmentOverview /></Suspense></ProtectedRoute>} />
        <Route path="assessment/red-team" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading red team audits..." />}><CustomAgentsRedTeam /></Suspense></ProtectedRoute>} />
        <Route path="assessment/red-team/scan" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading scan..." />}><RedTeamScanner /></Suspense></ProtectedRoute>} />
        <Route path="assessment/red-team/:scanId" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading report..." />}><AssessmentScanReport /></Suspense></ProtectedRoute>} />
        <Route path="assessment/red-team/:scanId/prompts" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading prompts..." />}><AssessmentScanPrompts /></Suspense></ProtectedRoute>} />
        <Route path="assessment/schedules" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading scheduled scans..." />}><CustomAgentsSchedules /></Suspense></ProtectedRoute>} />
        <Route path="assessment/evaluations" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading model evaluations..." />}><CustomAgentsEvaluations /></Suspense></ProtectedRoute>} />
        <Route path="assessment/evaluations/new" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback />}><ModelEvaluation /></Suspense></ProtectedRoute>} />
        <Route path="assessment/evaluations/:projectId" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback message="Loading results..." />}><AssessmentEvaluationReport /></Suspense></ProtectedRoute>} />
        <Route path="assessment/evaluations/:projectId/clone" element={<ProtectedRoute requiredModule="assessment"><Suspense fallback={<PageLoadingFallback />}><CloneEvaluation /></Suspense></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/custom-agents/dashboard" replace />} />
      </Route>
      {/* Agent Atlas Routes — unified enterprise view */}
      <Route element={<DateRangeProvider><Outlet /></DateRangeProvider>}>
        <Route
          path="/agent-atlas"
          element={<Navigate to="/agent-atlas/atlas" replace />}
        />
        <Route
          path="/agent-atlas/atlas"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Agent Atlas..." />}>
                  <AgentAtlas />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/inventory"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading inventory..." />}>
                  <AgentAtlasInventory serverPaged />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* The whole estate's Agent Network; each module mounts the same page pinned to its surface. */}
        <Route
          path="/agent-atlas/agent-network"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Agent Network..." />}>
                  <AgentNetwork />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/inbox"
          element={
            <ProtectedRoute requiredFeature="RECOMMENDATIONS">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading your inbox..." />}>
                  <AgentAtlasInbox />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* The page shipped briefly as /recommendations; keep the old path
            working for anyone who bookmarked it during the preview. Query is
            preserved because this page keeps its filters and the selected
            item in the URL — dropping it would land them on an unfiltered
            inbox instead of the view they saved. */}
        <Route
          path="/agent-atlas/recommendations"
          element={<RedirectPreserveQuery to="/agent-atlas/inbox" />}
        />
        <Route
          path="/agent-atlas/coverage"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading coverage..." />}>
                  <AgentAtlasCoverage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Security (Atlas) — consolidated AI-SOC / AI-SPM */}
        {[
          ['/security-atlas/overview', SAOverview],
          ['/security-atlas/incidents', SAIncidents],
          ['/security-atlas/incidents/:id', SAIncidentDetail],
          ['/security-atlas/signals', SASignals],
          ['/security-atlas/hunt', SAHunt],
          ['/security-atlas/skills', SASkills],
          // The deepfake detector belongs to Security (`deepfake` is an alias of it).
          ['/security-atlas/deepfake-detector', SADeepfakeDetector],
          ['/security-atlas/response', SAResponse],
          ['/security-atlas/automation', SAAutomation],
          // AI-BOM sits in the Discovery pillar, so Discovery licenses it.
          ['/security-atlas/ai-bom', SAAiBom, 'discovery'],
          ['/security-atlas/data-security', SADataSecurity],
          // MCP Security: preview, org 3 only, until MCP capture ships (MCP capture plan)
          ['/security-atlas/mcp-security', SAMcpSecurity, 'security', [3]],
          // Agent Instructions: preview, org 3 only, like MCP Security
          ['/security-atlas/agent-instructions', SAAgentInstructions, 'security', [3]],
          ['/security-atlas/coverage-drift', SACoverageDrift],
          ['/security-atlas/detection-health', SADetectionHealth],
          ['/security-atlas/exceptions', SAExceptions],
          ['/security-atlas/tuning', SATuning],
        ].map(([path, Comp, module = 'security', orgIds]) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute requiredModule={module} requiredOrgIds={orgIds}>
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading security..." />}>
                    <Comp />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
        ))}
        {/* Retired pages, folded into their neighbours — redirect old links/bookmarks. */}
        <Route path="/security-atlas/action-queue" element={<Navigate to="/security-atlas/incidents?sort=risk" replace />} />
        <Route path="/security-atlas/asset-coverage" element={<Navigate to="/security-atlas/ai-bom" replace />} />
        <Route path="/security-atlas/deepfake" element={<Navigate to="/security-atlas/deepfake-detector" replace />} />
        {/* Detections folded into the Skill Library (Sense skills) — the standalone page duplicated
            both the Skills catalog and the Overview's own detection-coverage section, and nav
            replaced it with Skills. Old links/bookmarks land on the canonical surface. */}
        <Route path="/security-atlas/detections" element={<Navigate to="/security-atlas/skills" replace />} />
        <Route
          path="/agent-atlas/users"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading users..." />}>
                  <AgentAtlasUsers />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Cross-Surface Intel was retired: identity across surfaces is Atlas →
            Users, the entity graph is AI-BOM, and the one artifact it uniquely
            owned (threat × surface) is now a card on Security Atlas Overview.
            Redirect so existing links and bookmarks still land somewhere real. */}
        <Route path="/agent-atlas/intel" element={<Navigate to="/agent-atlas/users" replace />} />
        {/* Responsible AI was retired (2026-10-26): its system register lives in Compliance's
            AI systems panel, so old links and bookmarks land there (?system= opens that system). */}
        <Route path="/agent-atlas/responsible-ai" element={<ResponsibleAiRedirect />} />
        {/* FinOps — the same page in two modes; `view` picks which of the two
            module pages the surface pills embed. */}
        <Route
          path="/agent-atlas/finops/tokenomics"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Tokenomics..." />}>
                  {/* key: both routes render this same component at the same
                      tree position, so without one React reuses the instance
                      and only swaps `view` — carrying the other view's surface
                      pill and fetched data across the navigation. */}
                  <AgentAtlasTokenomics />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/finops/usage-roi"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Usage & ROI..." />}>
                  <AgentAtlasRoi />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Licensing — seats bought against people active, idle cost, reclaim, renewals. */}
        <Route
          path="/agent-atlas/finops/licensing"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Licensing..." />}>
                  <AgentAtlasLicensing />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Budgets & Alerts — budgets on any scope and period, and who hears when one is
            crossed. Modules mount it pinned to their surface (?tab=budgets). */}
        <Route
          path="/agent-atlas/finops/budget"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Budgets & Alerts..." />}>
                  <AgentAtlasBudgets />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* FinOps settings: the calendar budgets reset by and alert defaults. Reached from
            the FinOps tab strip, not the sidebar. */}
        <Route
          path="/agent-atlas/finops/settings"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading FinOps settings..." />}>
                  <AgentAtlasFinOpsSettings />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Canonical FinOps entry. Renders Tokenomics content directly rather
            than redirecting: the sidebar has one FinOps link now, and a
            redirect on the way to the first view is a frame of nothing.
            /finops/tokenomics stays as an alias so existing links still work. */}
        <Route
          path="/agent-atlas/finops"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading FinOps..." />}>
                  <AgentAtlasTokenomics />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/policy"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Policy..." />}>
                  <AgentAtlasPolicy />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/agent-atlas/adoption" element={<Navigate to="/agent-atlas/adoption/conversations" replace />} />
        {/* The tab is "Unanswered Questions": its name as an address opens it. */}
        <Route path="/agent-atlas/adoption/unanswered" element={<RedirectPreserveQuery to="/agent-atlas/adoption/knowledge-gaps" />} />
        <Route
          path="/agent-atlas/adoption/conversations"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}>
                  <AgentAtlasConversations />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/adoption/knowledge-gaps"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Knowledge Gaps..." />}>
                  <AgentAtlasKnowledgeGaps />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/adoption/intent-analysis"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}>
                  <AgentAtlasIntentAnalysis />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Source intelligence across every surface (AI Adoption's fourth tab under "New navigation"). */}
        <Route
          path="/agent-atlas/adoption/sources"
          element={
            <ProtectedRoute requiredModule="finops">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Source Intelligence..." />}>
                  <AgentAtlasSources />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Assessment: red-team audits, scheduled scans and model evaluations across the platform,
            gated like the tools themselves (monitoring or compliance) as well as on Atlas. */}
        <Route
          path="/agent-atlas/assessment"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Assessment..." />}>
                  <AssessmentOverview />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* The Red Team and Model Evaluation screens, hosted inside Atlas until their Atlas
            rebuilds land, so following a scan or an evaluation never leaves Atlas. */}
        <Route
          path="/agent-atlas/assessment/red-team/scan"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading scan..." />}>
                  <RedTeamScanner />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/evaluations/new"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading..." />}>
                  <ModelEvaluation />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/evaluations/:projectId"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading results..." />}>
                  <AssessmentEvaluationReport />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/evaluations/:projectId/clone"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading..." />}>
                  <CloneEvaluation />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/red-team/:scanId"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading report..." />}>
                  <AssessmentScanReport />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/red-team/:scanId/prompts"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading prompts..." />}>
                  <AssessmentScanPrompts />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/red-team"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading red team audits..." />}>
                  <AssessmentRedTeam />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/schedules"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading scheduled scans..." />}>
                  <AssessmentSchedules />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/assessment/evaluations"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading model evaluations..." />}>
                  <AssessmentEvaluations />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/compliance"
          element={
            <ProtectedRoute requiredModule="governance">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}>
                  <AgentAtlasCompliance />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/compliance-audit"
          element={
            <ProtectedRoute requiredModule="governance">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Compliance Audit Report..." />}>
                  <AgentAtlasComplianceAudit />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/log-explorer"
          element={
            // Every surface's events in one stream, like each module's Log Explorer over its own.
            // Core module like the rest of Atlas: never module-locked; the service only returns
            // the org's own events.
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Log Explorer..." />}>
                  <AgentAtlasLogExplorer />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/alerts"
          element={
            // Agent Atlas is a core module: its pages, Notifications among
            // them, are never module-locked (each hides data from modules the
            // org has not bought instead).
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}>
                  <AgentAtlasAlerts />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* The agent LIST lives at /agent-atlas/inventory; /agent-atlas/agents/:agentId is one
            agent. Without these, /agent-atlas/agents (the collection) 404s and
            /agent-atlas/agents/registry — the path every other module uses for its list
            (/gemini/agents/registry, /copilot/studio/registry) — opened the detail page for an
            agent literally named "registry" and showed "Agent not found". Static segments outrank
            :agentId in React Router, so these win regardless of order. */}
        <Route path="/agent-atlas/agents" element={<RedirectPreserveQuery to="/agent-atlas/inventory" />} />
        {/* The Discovery pillar opens on its first page, AI Agents. */}
        <Route path="/agent-atlas/discovery" element={<RedirectPreserveQuery to="/agent-atlas/inventory" />} />
        <Route path="/agent-atlas/agents/registry" element={<RedirectPreserveQuery to="/agent-atlas/inventory" />} />
        <Route
          path="/agent-atlas/agents/:agentId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading agent..." />}>
                  <AgentAtlasAgentDetail />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-atlas/users/:email"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading user..." />}>
                  <AgentAtlasUserDetail />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Cloud — cross-cloud AI discovery. Lives in the same outer
            <DateRangeProvider> as Agent Atlas because every Cloud page
            takes a time-window filter and reuses the same provider
            contract. No feature flag — every page is the cloud_discovery
            module (locked, not hidden, for a tenant without it). */}
        <Route
          path="/cloud"
          element={<Navigate to="/cloud/overview" replace />}
        />
        <Route
          path="/cloud/overview"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading cloud overview..." />}>
                  <CloudOverview />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/inventory"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading workloads..." />}>
                  <CloudInventory />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Identities (was Principals): typed cloud identities. The old path keeps working. */}
        <Route path="/cloud/principals" element={<LegacyCloudPrincipalsRedirect />} />
        <Route
          path="/cloud/identities"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading identities..." />}>
                  <CloudPrincipals />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* One identity's / agent's full screen: the Atlas User 360 and Agent 360, scoped to
            the three clouds. */}
        <Route element={<SurfaceScope surface="cloud-discovery" />}>
          <Route
            path="/cloud/agents/:agentId"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading agent..." />}><AgentAtlasAgentDetail /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/identities/:email"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading identity..." />}><AgentAtlasUserDetail /></Suspense></AppLayout></ProtectedRoute>}
          />
        </Route>
        {/* Atlas Policy pinned to cloud: the posture catalog's checks as detect-only policy. */}
        <Route
          path="/cloud/policy"
          element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading policy..." />}><AgentAtlasPolicy lockedSurface="cloud-discovery" /></Suspense></AppLayout></ProtectedRoute>}
        />
        <Route
          path="/cloud/models"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading models..." />}>
                  <CloudModels />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/agents"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading agents..." />}>
                  <CloudAgents />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/knowledge-bases"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading knowledge bases..." />}>
                  <CloudKnowledgeBases />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/prompts"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading prompts..." />}>
                  <CloudPrompts />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Notifications: the Atlas Alerts page pinned to Cloud Discovery (alert module
            cloud_discovery). The old path lands here. */}
        <Route
          path="/cloud/notifications"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading notifications..." />}>
                  <AgentAtlasAlerts
                    lockedSurface="cloud-discovery"
                    title="Notifications"
                    subtitle="Alerts raised for your cloud AI estate — new agents discovered, rule matches and delivery to your channels."
                  />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/cloud/security-events" element={<Navigate to="/cloud/notifications" replace />} />
        {/* Guardrail outcomes (Bedrock Guardrails / Azure content filter / Model Armor), which
            the old Alerts link showed. Kept reachable; no longer in the nav. */}
        <Route
          path="/cloud/guardrails"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading guardrail events..." />}>
                  <CloudSecurityEvents />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/posture"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading AI posture..." />}>
                  <CloudPosture />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/changes"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading estate changes..." />}>
                  <CloudChanges />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/topology"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading AI topology..." />}>
                  <CloudTopology />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/recommendations"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Recommendations..." />}>
                  <AgentAtlasInbox lockedModule="cloud" />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/usage-roi"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Usage & ROI..." />}>
                  <CloudOverview pageTitle="Usage & ROI" pageSubtitle="Cloud AI adoption, activity, and estate value across providers" />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/intent-analysis"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}>
                  <CloudPrompts pageTitle="Intent Analysis" pageSubtitle="Analyze the actions and intent patterns observed across cloud AI services" />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/compliance"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}>
                  {/* The shared module Compliance page, as Code Security mounts it: the posture
                      checks' standards as framework controls (agent-atlas compliance_cloud). */}
                  <CloudCompliance />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud/log-explorer"
          element={
            <ProtectedRoute requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Log Explorer..." />}>
                  {/* Who changed the cloud AI estate: the providers' control-plane audit,
                      narrowed to changes to AI resources (monitoring /cloud/audit-trail). */}
                  <CloudAuditLog />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Audit Log was renamed Log Explorer and moved out of the Compliance group (2026-10-10); old links keep their query. */}
        <Route path="/cloud/audit-log" element={<RedirectPreserveLocation to="/cloud/log-explorer" />} />
        {/* Cloud Discovery has no Settings page (it only linked to pages reachable on their own). */}
        <Route path="/cloud/settings" element={<Navigate to="/cloud/overview" replace />} />
        {/* Shared Atlas security workspace, narrowed to Cloud Discovery. The
            empty-state fallback keeps these pages useful while individual
            cloud security read contracts are rolled out incrementally. */}
        <Route element={<SurfaceScope surface="cloud-discovery" emptyOnUnavailable />}>
          <Route
            path="/cloud/security/signals"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}><SASignals /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/security/hunt"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}><SAHunt /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/security/incidents"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}><SAIncidents /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/security/incidents/:id"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}><SAIncidentDetail /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/security/skills"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}><SASkills /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/security/response"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading Response..." />}><SAResponse /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/security/exceptions"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}><SAExceptions /></Suspense></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/cloud/security/ai-bom"
            element={<ProtectedRoute requiredModule="discovery"><AppLayout><Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}><SAAiBom /></Suspense></AppLayout></ProtectedRoute>}
          />
        </Route>
        <Route
          path="/cloud/workloads"
          element={<LegacyCloudWorkloadsRedirect />}
        />
      </Route>

      {/* M365 Copilot Routes — nested under CopilotLayout (injects service + labels + date range + filter contexts) */}
      <Route
        path="/copilot"
        element={
          <ProtectedRoute requiredModule="copilot">
            <AppLayout>
              <CopilotLayout />
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoadingFallback message="Loading Copilot Dashboard..." />}><CopilotDashboard /></Suspense>} />
        <Route path="recommendations" element={<Suspense fallback={<PageLoadingFallback message="Loading Recommendations..." />}><AgentAtlasInbox lockedModule="copilot" /></Suspense>} />
        <Route path="agent-network" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Network..." />}><AgentNetwork lockedSurface="copilot" /></Suspense>} />
        <Route path="usage-roi" element={<Suspense fallback={<PageLoadingFallback message="Loading Usage & ROI..." />}><ModuleFinOpsTabs surface="copilot" /></Suspense>} />
        <Route path="users" element={<Suspense fallback={<PageLoadingFallback message="Loading Users..." />}><CopilotUsers /></Suspense>} />
        <Route path="knowledge-gaps" element={<Suspense fallback={<PageLoadingFallback message="Loading Knowledge Gaps..." />}><CopilotKnowledgeGaps /></Suspense>} />
        <Route path="intent-analysis" element={<Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}><CopilotIntentAnalysis /></Suspense>} />
        <Route path="conversations" element={<Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}><CopilotConversations /></Suspense>} />
        {/* Threats is retired for the Security Signals view of this surface (2026-10-17) */}
        <Route path="threats" element={<ThreatsToSignals base="/copilot" />} />
        <Route path="posture" element={<Suspense fallback={<PageLoadingFallback message="Loading Access Management..." />}><CopilotPosture /></Suspense>} />
        <Route path="sources" element={<Suspense fallback={<PageLoadingFallback message="Loading Source Intelligence..." />}><CopilotSources /></Suspense>} />
        <Route path="registry" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Registry..." />}><CopilotRegistry /></Suspense>} />
        {/* Agent 360 and User 360 mounted in the module, pinned to M365 Copilot. */}
        <Route element={<SurfaceScope surface="copilot" atlasSurface="copilot" />}>
          <Route path="registry/:agentId" element={<Suspense fallback={<PageLoadingFallback message="Loading agent..." />}><AgentAtlasAgentDetail /></Suspense>} />
          <Route path="users/:email" element={<Suspense fallback={<PageLoadingFallback message="Loading user..." />}><AgentAtlasUserDetail /></Suspense>} />
        </Route>
        <Route path="applications" element={<RedirectPreserveLocation to="/copilot/registry" />} />
        <Route path="log-explorer" element={<Suspense fallback={<PageLoadingFallback message="Loading Log Explorer..." />}><CopilotAuditLog /></Suspense>} />
        {/* Audit Log was renamed Log Explorer and moved out of the Compliance group (2026-10-10); old links keep their query. */}
        <Route path="audit-log" element={<RedirectPreserveLocation to="/copilot/log-explorer" />} />
        <Route path="compliance" element={<Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}><CopilotCompliance /></Suspense>} />
        <Route path="audit-report" element={<Suspense fallback={<PageLoadingFallback message="Loading Audit Report..." />}><CopilotAuditReport /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoadingFallback message="Loading Settings..." />}><CopilotSettings /></Suspense>} />
        <Route path="alerts" element={<Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}><CopilotAlerts /></Suspense>} />
        <Route element={<SurfaceScope surface="copilot" emptyOnUnavailable />}>
          <Route path="security/signals" element={<Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}><SASignals /></Suspense>} />
          <Route path="security/hunt" element={<Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}><SAHunt /></Suspense>} />
          <Route path="security/incidents" element={<Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}><SAIncidents /></Suspense>} />
          <Route path="security/incidents/:id" element={<Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}><SAIncidentDetail /></Suspense>} />
          <Route path="security/skills" element={<Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}><SASkills /></Suspense>} />
          <Route path="security/response" element={<Suspense fallback={<PageLoadingFallback message="Loading Response..." />}><SAResponse /></Suspense>} />
          <Route path="security/exceptions" element={<Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}><SAExceptions /></Suspense>} />
          <Route path="security/ai-bom" element={<Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}><SAAiBom /></Suspense>} />
        </Route>
        {/* Backward-compat redirects for old paths */}
        <Route path="interactions" element={<Navigate to="/copilot/conversations" replace />} />
      </Route>
      {/* Gemini Workspace Routes — nested under GeminiWorkspaceLayout (DateRange + AgentFilter + AgentService + AgentLabels) */}
      <Route
        path="/gemini"
        element={
          <ProtectedRoute requiredModule="gemini">
            <AppLayout>
              <GeminiWorkspaceLayout />
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoadingFallback message="Loading Gemini Dashboard..." />}><GeminiDashboard /></Suspense>} />
        <Route path="conversations" element={<Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}><GeminiInteractions /></Suspense>} />
        <Route path="interactions" element={<Navigate to="/gemini/conversations" replace />} />
        <Route path="threats" element={<ThreatsToSignals base="/gemini" />} />
        {/* Security - the Atlas Signals view, pinned to Gemini Workspace (the security service's
            `gemini` surface), as every other module inherits it */}
        <Route element={<SurfaceScope surface="gemini" emptyOnUnavailable />}>
          <Route path="security/signals" element={<Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}><SASignals /></Suspense>} />
        </Route>
        <Route path="users" element={<Suspense fallback={<PageLoadingFallback message="Loading Users..." />}><GeminiUsers /></Suspense>} />
        <Route path="applications" element={<Suspense fallback={<PageLoadingFallback message="Loading Applications..." />}><GeminiApplications /></Suspense>} />
        {/* Agent 360 and User 360 mounted in the module, pinned to Gemini Workspace (the security
            plane's `gemini`, Atlas's `gemini-workspace`). Its agents are the Workspace
            applications Gemini answers in, so one opens from Applications. */}
        <Route element={<SurfaceScope surface="gemini" atlasSurface="gemini-workspace" />}>
          <Route path="applications/:agentId" element={<Suspense fallback={<PageLoadingFallback message="Loading agent..." />}><AgentAtlasAgentDetail /></Suspense>} />
          <Route path="users/:email" element={<Suspense fallback={<PageLoadingFallback message="Loading user..." />}><AgentAtlasUserDetail /></Suspense>} />
        </Route>
        <Route path="intent-analysis" element={<Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}><GeminiIntentAnalysis /></Suspense>} />
        <Route path="knowledge-gaps" element={<Suspense fallback={<PageLoadingFallback message="Loading Knowledge Gaps..." />}><GeminiKnowledgeGaps /></Suspense>} />
        {/* Sessions duplicated Conversations and nothing linked to it (removed 2026-10-22). */}
        <Route path="sessions" element={<RedirectPreserveLocation to="/gemini/conversations" />} />
        <Route path="usage-roi" element={<Suspense fallback={<PageLoadingFallback message="Loading Usage & ROI..." />}><GeminiUsageROI /></Suspense>} />
        <Route path="log-explorer" element={<Suspense fallback={<PageLoadingFallback message="Loading Log Explorer..." />}><GeminiAuditLog /></Suspense>} />
        {/* Audit Log was renamed Log Explorer, as in every module; old links keep their query. */}
        <Route path="audit-log" element={<RedirectPreserveLocation to="/gemini/log-explorer" />} />
        <Route path="compliance" element={<Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}><GeminiCompliance /></Suspense>} />
        <Route path="audit-report" element={<Suspense fallback={<PageLoadingFallback message="Loading Audit Report..." />}><GeminiAuditReport /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoadingFallback message="Loading Settings..." />}><GeminiSettings /></Suspense>} />
        <Route path="alerts" element={<Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}><GeminiAlerts /></Suspense>} />
        {/* Backward-compat: registry → applications page for workspace */}
        <Route path="registry" element={<Navigate to="/gemini/applications" replace />} />
      </Route>
      {/* Gemini Agents — nested routes with shared DateRange context */}
      <Route
        path="/gemini/agents"
        element={
          <ProtectedRoute requiredModule="gemini_enterprise">
            <AppLayout>
              <GeminiAgentsLayout />
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoadingFallback message="Loading Agents Dashboard..." />}><GeminiEnterpriseDashboard /></Suspense>} />
        <Route path="conversations" element={<Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}><GeminiEnterpriseConversations /></Suspense>} />
        <Route path="intent-analysis" element={<Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}><GeminiEnterpriseIntentAnalysis /></Suspense>} />
        <Route path="sources" element={<Suspense fallback={<PageLoadingFallback message="Loading Source Intelligence..." />}><GeminiEnterpriseSources /></Suspense>} />
        {/* Applications re-read the Registry's agent list and nothing linked to it (removed 2026-10-22). */}
        <Route path="applications" element={<RedirectPreserveLocation to="/gemini/agents/registry" />} />
        <Route path="registry" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Registry..." />}><GeminiEnterpriseRegistry /></Suspense>} />
        <Route path="log-explorer" element={<Suspense fallback={<PageLoadingFallback message="Loading Log Explorer..." />}><GeminiEnterpriseAuditLog /></Suspense>} />
        {/* Audit Log was renamed Log Explorer (2026-10-05); old links keep their query. */}
        <Route path="audit-log" element={<RedirectPreserveLocation to="/gemini/agents/log-explorer" />} />
        <Route path="knowledge-gaps" element={<Suspense fallback={<PageLoadingFallback message="Loading Knowledge Gaps..." />}><GeminiEnterpriseKnowledgeGaps /></Suspense>} />
        {/* Coverage Atlas overlapped Knowledge Gaps and nothing linked to it (removed 2026-10-22). */}
        <Route path="coverage-atlas" element={<RedirectPreserveLocation to="/gemini/agents/knowledge-gaps" />} />
        {/* Usage & ROI carries Tokenomics as a sub-tab; the old tokenomics URL
            keeps working as a link into that tab. */}
        <Route path="usage-roi" element={<Suspense fallback={<PageLoadingFallback message="Loading Usage & ROI..." />}><GeminiEnterpriseUsageRoi /></Suspense>} />
        <Route path="tokenomics" element={<Navigate to="/gemini/agents/usage-roi?tab=tokenomics" replace />} />
        <Route path="users" element={<Suspense fallback={<PageLoadingFallback message="Loading Users..." />}><GeminiEnterpriseUsers /></Suspense>} />
        {/* Compliance and its Audit Report: two pages, the two rows of the sidebar's Compliance
            group (an old ?tab=audit-report link lands on the report); compliance-tabs stays
            routable for any bookmark that used it. */}
        <Route path="compliance" element={<Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}><GeminiEnterpriseCompliance /></Suspense>} />
        <Route path="audit-report" element={<Suspense fallback={<PageLoadingFallback message="Loading Audit Report..." />}><GeminiEnterpriseAuditReport /></Suspense>} />
        <Route path="compliance-tabs" element={<Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}><GeminiEnterpriseCompliance /></Suspense>} />
        <Route path="posture" element={<Suspense fallback={<PageLoadingFallback message="Loading Access Management..." />}><GeminiEnterpriseAccess /></Suspense>} />
        <Route path="policy" element={<Suspense fallback={<PageLoadingFallback message="Loading Policy..." />}><GeminiEnterprisePolicy /></Suspense>} />
        <Route path="violations" element={<Suspense fallback={<PageLoadingFallback message="Loading Policy Violations..." />}><GeminiAgentsPolicyViolations /></Suspense>} />
        {/* Grounding Analytics — hidden */}
        <Route path="agent-network" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Network..." />}><AgentNetwork lockedSurface="gemini-enterprise" entityScope /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoadingFallback message="Loading Settings..." />}><GeminiEnterpriseSettings /></Suspense>} />
        <Route path="alerts" element={<Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}><GeminiEnterpriseAlerts /></Suspense>} />

        {/* Security — the Atlas views, pinned to this surface. Nothing here is a
            Gemini-specific copy: the same components render under
            /security-atlas/* unscoped, and SurfaceScope narrows every API call
            they make. Recommendations is the same page with its module locked. */}
        <Route element={<SurfaceScope surface="gemini_enterprise" atlasSurface="gemini-enterprise" />}>
          {/* Atlas's agent and user detail pages, mounted here rather than copied.
              Same components, so a change in Atlas reaches Gemini automatically —
              the pattern the security views already use. The path segment differs
              because Gemini calls its agent list "registry". */}
          <Route path="registry/:agentId" element={<Suspense fallback={<PageLoadingFallback message="Loading agent..." />}><AgentAtlasAgentDetail /></Suspense>} />
          <Route path="users/:email" element={<Suspense fallback={<PageLoadingFallback message="Loading user..." />}><AgentAtlasUserDetail /></Suspense>} />
          <Route path="security/signals" element={<Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}><SASignals entityScope /></Suspense>} />
          {/* Detections folded into Skills (Sense skills) — old link redirects to the canonical surface. */}
          <Route path="security/detections" element={<Navigate to="/gemini/agents/security/skills" replace />} />
          <Route path="security/incidents" element={<Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}><SAIncidents entityScope /></Suspense>} />
          <Route path="security/incidents/:id" element={<Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}><SAIncidentDetail /></Suspense>} />
          <Route path="security/hunt" element={<Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}><SAHunt entityScope /></Suspense>} />
          <Route path="security/skills" element={<Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}><SASkills /></Suspense>} />
          <Route path="security/response" element={<Suspense fallback={<PageLoadingFallback message="Loading Response..." />}><SAResponse /></Suspense>} />
          <Route path="security/exceptions" element={<Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}><SAExceptions /></Suspense>} />
          <Route path="security/ai-bom" element={<Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}><SAAiBom /></Suspense>} />
        </Route>
        <Route path="recommendations" element={<Suspense fallback={<PageLoadingFallback message="Loading Recommendations..." />}><AgentAtlasInbox lockedModule="gemini" /></Suspense>} />
      </Route>
      {/* Copilot Studio Routes — nested under CopilotStudioLayout (injects service + label contexts) */}
      <Route
        path="/copilot/studio"
        element={
          <ProtectedRoute requiredModule="copilot_studio">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Copilot Studio..." />}>
                <CopilotStudioLayout />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoadingFallback message="Loading Studio Dashboard..." />}><CopilotStudioDashboard /></Suspense>} />
        <Route path="recommendations" element={<Suspense fallback={<PageLoadingFallback message="Loading Recommendations..." />}><AgentAtlasInbox lockedModule="copilot_studio" /></Suspense>} />
        <Route path="agent-network" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Network..." />}><AgentNetwork lockedSurface="copilot-studio" /></Suspense>} />
        <Route path="conversations" element={<Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}><CopilotStudioConversations /></Suspense>} />
        <Route path="threats" element={<ThreatsToSignals base="/copilot/studio" />} />
        <Route path="posture" element={<Suspense fallback={<PageLoadingFallback message="Loading Security Posture..." />}><CopilotStudioPosture /></Suspense>} />
        <Route path="intent-analysis" element={<Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}><CopilotStudioIntentAnalysis /></Suspense>} />
        <Route path="knowledge-gaps" element={<Suspense fallback={<PageLoadingFallback message="Loading Knowledge Gaps..." />}><CopilotStudioKnowledgeGaps /></Suspense>} />
        <Route path="usage-roi" element={<Suspense fallback={<PageLoadingFallback message="Loading Usage & ROI..." />}><ModuleFinOpsTabs surface="copilot-studio" /></Suspense>} />
        <Route path="sources" element={<Suspense fallback={<PageLoadingFallback message="Loading Source Intelligence..." />}><CopilotStudioSources /></Suspense>} />
        <Route path="registry" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Registry..." />}><CopilotStudioRegistry /></Suspense>} />
        {/* Agent 360 and User 360 mounted in the module, pinned to Copilot Studio. */}
        <Route element={<SurfaceScope surface="copilot_studio" atlasSurface="copilot-studio" />}>
          <Route path="registry/:agentId" element={<Suspense fallback={<PageLoadingFallback message="Loading agent..." />}><AgentAtlasAgentDetail /></Suspense>} />
          <Route path="users/:email" element={<Suspense fallback={<PageLoadingFallback message="Loading user..." />}><AgentAtlasUserDetail /></Suspense>} />
        </Route>
        <Route path="applications" element={<RedirectPreserveLocation to="/copilot/studio/registry" />} />
        <Route path="log-explorer" element={<Suspense fallback={<PageLoadingFallback message="Loading Log Explorer..." />}><CopilotStudioAuditLog /></Suspense>} />
        {/* Audit Log was renamed Log Explorer and moved out of the Compliance group (2026-10-10); old links keep their query. */}
        <Route path="audit-log" element={<RedirectPreserveLocation to="/copilot/studio/log-explorer" />} />
        <Route path="users" element={<Suspense fallback={<PageLoadingFallback message="Loading Users..." />}><CopilotStudioUsers /></Suspense>} />
        <Route path="compliance" element={<Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}><CopilotStudioCompliance /></Suspense>} />
        <Route path="audit-report" element={<Suspense fallback={<PageLoadingFallback message="Loading Audit Report..." />}><CopilotStudioAuditReport /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoadingFallback message="Loading Settings..." />}><CopilotStudioSettings /></Suspense>} />
        <Route path="alerts" element={<Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}><CopilotStudioAlerts /></Suspense>} />
        <Route element={<SurfaceScope surface="copilot_studio" emptyOnUnavailable />}>
          <Route path="security/signals" element={<Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}><SASignals /></Suspense>} />
          <Route path="security/hunt" element={<Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}><SAHunt /></Suspense>} />
          <Route path="security/incidents" element={<Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}><SAIncidents /></Suspense>} />
          <Route path="security/incidents/:id" element={<Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}><SAIncidentDetail /></Suspense>} />
          <Route path="security/skills" element={<Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}><SASkills /></Suspense>} />
          <Route path="security/response" element={<Suspense fallback={<PageLoadingFallback message="Loading Response..." />}><SAResponse /></Suspense>} />
          <Route path="security/exceptions" element={<Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}><SAExceptions /></Suspense>} />
          <Route path="security/ai-bom" element={<Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}><SAAiBom /></Suspense>} />
        </Route>
      </Route>
      {/* Unified SaaS-AI module — one surface, vendor chosen via ?vendor= (OpenAI, Anthropic, …).
          Pages live in src/pages/saas-ai and query /<vendor>/api/v1 via saasAiClient + vendorStore.
          Each provider is its own module (openai / anthropic / cursor / google_antigravity), so
          the gate is per vendor: SaasVendorLockOutlet shows the lock screen for the ACTIVE
          vendor when the org lacks it, and the vendor switcher stays usable above it. */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout>
              <SaasVendorProvider>
                <DateRangeProvider>
                  <SaasAtlasWindow>
                    <SaasControlBar />
                    <SaasVendorLockOutlet>
                      <SaasSurfaceOutlet />
                    </SaasVendorLockOutlet>
                  </SaasAtlasWindow>
                </DateRangeProvider>
              </SaasVendorProvider>
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route path="/saas-ai/dashboard" element={<Suspense fallback={<PageLoadingFallback message="Loading dashboard..." />}><AiProvidersDashboard /></Suspense>} />
        <Route path="/saas-ai/recommendations" element={<Suspense fallback={<PageLoadingFallback message="Loading Recommendations..." />}><SaasRecommendations /></Suspense>} />
        <Route path="/saas-ai/agent-network" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Network..." />}><SaasAgentNetwork /></Suspense>} />
        <Route path="/saas-ai/registry" element={<Suspense fallback={<PageLoadingFallback message="Loading Agent Registry..." />}><AiProvidersRegistry /></Suspense>} />
        <Route path="/saas-ai/conversations" element={<Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}><AiProvidersConversations /></Suspense>} />
        {/* Notifications carries the scanner detections (the former Threats page) as its
            Detections view: /saas-ai/alerts?view=detections. */}
        <Route path="/saas-ai/alerts" element={<Suspense fallback={<PageLoadingFallback message="Loading Notifications..." />}><AiProvidersAlerts /></Suspense>} />
        <Route path="/saas-ai/users" element={<Suspense fallback={<PageLoadingFallback message="Loading users..." />}><AiProvidersUsers /></Suspense>} />
        {/* Models merged into the registry — redirect old path */}
        <Route path="/saas-ai/models" element={<Navigate to="/saas-ai/registry" replace />} />
        <Route path="/saas-ai/usage" element={<Navigate to="/saas-ai/usage-roi" replace />} />
        <Route path="/saas-ai/intent-analysis" element={<Suspense fallback={<PageLoadingFallback message="Loading intent analysis..." />}><AiProvidersIntentAnalysis /></Suspense>} />
        <Route path="/saas-ai/knowledge-gaps" element={<Suspense fallback={<PageLoadingFallback message="Loading knowledge gaps..." />}><AiProvidersKnowledgeGaps /></Suspense>} />
        <Route path="/saas-ai/sources" element={<Suspense fallback={<PageLoadingFallback message="Loading Source Intelligence..." />}><AiProvidersRegistry pageTitle="Source Intelligence" pageSubtitle="Inspect the models, providers, and endpoints supplying this AI surface." /></Suspense>} />
        <Route path="/saas-ai/usage-roi" element={<Suspense fallback={<PageLoadingFallback message="Loading usage & costs..." />}><SaasFinOpsTabs /></Suspense>} />
        <Route path="/saas-ai/log-explorer" element={<Suspense fallback={<PageLoadingFallback message="Loading log explorer..." />}><AiProvidersAuditLog /></Suspense>} />
        {/* Audit Log was renamed Log Explorer and moved out of the Compliance group (2026-10-10); old links keep their query. */}
        <Route path="/saas-ai/audit-log" element={<RedirectPreserveLocation to="/saas-ai/log-explorer" />} />
        {/* Compliance and its Audit Report: two pages, the two rows of the sidebar's Compliance group. */}
        <Route path="/saas-ai/compliance" element={<Suspense fallback={<PageLoadingFallback message="Loading compliance..." />}><AiProvidersCompliance /></Suspense>} />
        <Route path="/saas-ai/audit-report" element={<Suspense fallback={<PageLoadingFallback message="Loading Audit Report..." />}><AiProvidersAuditReport /></Suspense>} />
        <Route path="/saas-ai/policy" element={<Suspense fallback={<PageLoadingFallback message="Loading policy..." />}><SaasAiPolicy /></Suspense>} />
        <Route path="/saas-ai/settings" element={<Suspense fallback={<PageLoadingFallback message="Loading settings..." />}><AiProvidersSettings /></Suspense>} />
        {/* No posture API exists for SaaS providers yet; Access Management shows the provider's
            settings under that title, as it always has. */}
        <Route path="/saas-ai/posture" element={<Suspense fallback={<PageLoadingFallback message="Loading Access Management..." />}><AiProvidersSettings pageTitle="Access Management" pageSubtitle="Configure provider access, risk thresholds, and governance controls." /></Suspense>} />
        {/* The same Atlas security workspaces used by Gemini Enterprise, scoped
            to whichever AI provider is active in SaasVendorContext. Keeping one
            component set prevents the product and Atlas views from drifting. */}
        <Route element={<SaasSecurityScope />}>
          {/* Atlas's agent and user detail pages, mounted in the module (as Gemini Enterprise
              does) so a registry or users row stays inside /saas-ai. */}
          <Route path="/saas-ai/registry/:agentId" element={<Suspense fallback={<PageLoadingFallback message="Loading agent..." />}><AgentAtlasAgentDetail /></Suspense>} />
          <Route path="/saas-ai/users/:email" element={<Suspense fallback={<PageLoadingFallback message="Loading user..." />}><AgentAtlasUserDetail /></Suspense>} />
          <Route path="/saas-ai/security/signals" element={<Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}><SASignals /></Suspense>} />
          <Route path="/saas-ai/security/hunt" element={<Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}><SAHunt /></Suspense>} />
          <Route path="/saas-ai/security/incidents" element={<Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}><SAIncidents /></Suspense>} />
          <Route path="/saas-ai/security/incidents/:id" element={<Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}><SAIncidentDetail /></Suspense>} />
          <Route path="/saas-ai/security/skills" element={<Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}><SASkills /></Suspense>} />
          <Route path="/saas-ai/security/response" element={<Suspense fallback={<PageLoadingFallback message="Loading Response..." />}><SAResponse /></Suspense>} />
          <Route path="/saas-ai/security/exceptions" element={<Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}><SAExceptions /></Suspense>} />
          <Route path="/saas-ai/security/ai-bom" element={<Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}><SAAiBom /></Suspense>} />
        </Route>
      </Route>
      {/* SAST (Code Security) Routes */}
      {/* Code Security, redesigned 2026-09-30: Overview, Repositories (repos and uploads,
          scan history, new scans), Findings, a scan's code graph at /sast/scans/:scanId,
          Security, Policy, Settings. The old paths below redirect. */}
      <Route path="/sast" element={<Navigate to="/sast/overview" replace />} />
      <Route path="/sast/dashboard" element={<SASTRedirect to="/sast/overview" />} />
      <Route
        path="/sast/overview"
        element={
          <ProtectedRoute requiredModule="assessment">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Code Security..." />}>
                <SASTOverview />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sast/repositories"
        element={
          <ProtectedRoute requiredModule="assessment">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Repositories..." />}>
                <SASTRepositories />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sast/findings"
        element={
          <ProtectedRoute requiredModule="assessment">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Findings Report..." />}>
                <SASTFindings />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* The Atlas Inbox pinned to the engine's `sast` module: Code Security findings only.
          Same pre-GA gate as the Atlas Inbox (the recommendation service). */}
      <Route
        path="/sast/recommendations"
        element={
          <ProtectedRoute requiredModule="assessment" requiredFeature="RECOMMENDATIONS">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Recommendations..." />}>
                <SASTRecommendations />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/sast/history" element={<SASTRedirect to="/sast/repositories" />} />
      <Route path="/sast/scanner" element={<SASTRedirectNewScan />} />
      <Route path="/sast/scanner/:scanId" element={<SASTRedirectScan />} />
      <Route
        path="/sast/scans/:scanId"
        element={
          <ProtectedRoute requiredModule="assessment">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading scan..." />}>
                <SASTScanner />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sast/github/callback"
        element={
          <ProtectedRoute requiredModule="assessment">
            <Suspense fallback={<PageLoadingFallback message="Finalizing GitHub connection..." />}>
              <SASTGitHubCallback />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sast/settings"
        element={
          <ProtectedRoute requiredModule="assessment">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Settings..." />}>
                <SASTSettings />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sast/policy"
        element={
          <ProtectedRoute requiredModule="assessment">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Security Policy..." />}>
                <SASTPolicy />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sast/compliance"
        element={
          <ProtectedRoute requiredModule="assessment">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}>
                {/* The shared module compliance page (as Endpoint Security mounts it),
                    driven by the sast service over agent-atlas compliance_sast. */}
                <SASTCompliance />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Code Security's Security group: the shared Atlas security pages pinned to the
          SAST surface (index saf3ai-sast-findings; incidents whose surfaces include
          "sast") through the security service's "sast" scope - the way Endpoint
          Security, Gemini and Copilot Studio inherit theirs. Added 2026-09-30. */}
      <Route element={<SurfaceScope surface="sast" emptyOnUnavailable />}>
        <Route
          path="/sast/security/signals"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}>
                  <SASignals />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sast/security/hunt"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}>
                  <SAHunt />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sast/security/incidents"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}>
                  <SAIncidents />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sast/security/incidents/:id"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}>
                  <SAIncidentDetail />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sast/security/skills"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}>
                  <SASkills />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sast/security/response"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Response..." />}>
                  <SAResponse />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sast/security/exceptions"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}>
                  <SAExceptions />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sast/security/ai-bom"
          element={
            <ProtectedRoute requiredModule="assessment">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}>
                  <SAAiBom />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="/conversation-explorer"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <ConversationExplorer />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent-network"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AgentNetwork lockedSurface="custom" />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-behavior"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <UserActivity />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Alerts moved under Agent Atlas — redirect preserves query string
          (e.g. ?alert_id=123 from email "View in Saf3AI" links). */}
      <Route path="/alerts" element={<RedirectPreserveQuery to="/agent-atlas/alerts" />} />
      <Route
        path="/model-evaluation"
        element={
          <ProtectedRoute requiredModule="assessment">
            <LegacyAssessmentRoute to={`${CA_ASSESSMENT}/evaluations/new`}>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <ModelEvaluation />
                </Suspense>
              </AppLayout>
            </LegacyAssessmentRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/model-evaluation/clone/:projectId"
        element={
          <ProtectedRoute requiredModule="assessment">
            <LegacyAssessmentRoute to={({ projectId }) => `${CA_ASSESSMENT}/evaluations/${encodeURIComponent(projectId)}/clone`}>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <CloneEvaluation />
                </Suspense>
              </AppLayout>
            </LegacyAssessmentRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/model-evaluation/projects"
        element={
          <ProtectedRoute requiredModule="assessment">
            <LegacyAssessmentRoute to={`${CA_ASSESSMENT}/evaluations`}>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <EvaluationProjects />
                </Suspense>
              </AppLayout>
            </LegacyAssessmentRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/model-evaluation/arena/:projectId"
        element={
          <ProtectedRoute requiredModule="assessment">
            <LegacyAssessmentRoute to={({ projectId }) => `${CA_ASSESSMENT}/evaluations/${encodeURIComponent(projectId)}`}>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <EvaluationArena />
                </Suspense>
              </AppLayout>
            </LegacyAssessmentRoute>
          </ProtectedRoute>
        }
      />
      {/* FinOps Routes - More specific routes first */}
      <Route
        path="/finops/simulator"
        element={<RedirectPreserveLocation to="/custom-agents/finops" />}
      />
      <Route
        path="/finops/tokenomics"
        element={<RedirectPreserveLocation to="/finops/usage-roi?tab=tokenomics" />}
      />
      <Route
        path="/finops/usage-roi"
        element={
          <ProtectedRoute requiredModule="finops">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Usage & ROI..." />}>
                <CustomAgentsUsageRoi />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Former name — kept so existing links and bookmarks keep resolving. */}
      <Route path="/finops/roi" element={<RedirectPreserveLocation to="/finops/usage-roi?tab=usage-roi" />} />
      <Route
        path="/finops/budget"
        element={<RedirectPreserveLocation to="/custom-agents/finops" />}
      />
      <Route
        path="/finops/allocation"
        element={<RedirectPreserveLocation to="/custom-agents/finops" />}
      />
      <Route
        path="/finops/greenops"
        element={<RedirectPreserveLocation to="/custom-agents/finops" />}
      />
      <Route
        path="/finops/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Settings..." />}>
                <CustomAgentsSettings />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* "LLM Cost Management" is now Tokenomics — the first view of Custom
          Agents FinOps. Filters on the old URL survive the hop. */}
      <Route path="/finops" element={<RedirectPreserveLocation to="/custom-agents/finops" />} />
      {/* Security and FinOps notifications are sources on the consolidated Notifications page (no
          surface owns them); their old list addresses open it filtered to that source. */}
      <Route
        path="/finops/alerts"
        element={<RedirectPreserveLocation to="/agent-atlas/alerts?surface=finops" />}
      />
      <Route
        path="/security/alerts"
        element={<RedirectPreserveLocation to="/agent-atlas/alerts?surface=security" />}
      />
      <Route
        path="/observability/alerts"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}>
                <CustomAgentsAlerts />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* SAST notifications are a separate config store (sastAPI), not alert-microservice data — no list page. */}
      <Route path="/sast/alerts" element={<Navigate to="/admin/alert-configuration?tab=routing" replace />} />
      <Route path="/admin/endpoint-alerts" element={<Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}><AdminEndpointAlerts /></Suspense>} />
      <Route path="/admin/alert-routing" element={<Navigate to="/admin/alert-configuration?tab=routing" replace />} />

      {/* Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <ManageUsers />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents"
        element={
          <ForwardEvaluationsTab>
            <ProtectedRoute requiredRole="Administrator">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <ManageAgents />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          </ForwardEvaluationsTab>
        }
      />
      <Route
        path="/admin/teams"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <ManageTeams />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/custom-roles"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomRoles />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/custom-roles/new"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomRoleEditor />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/custom-roles/:roleId/edit"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomRoleEditor />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/custom-roles/:roleId/view"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomRoleEditor />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recommendation-rules"
        element={
          <ProtectedRoute requiredRole="Administrator" requiredOrgIds={[3]}>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <RecommendationRules />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/policies"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomAgentsPolicy />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Agent Atlas' policy/planes API hands off "Custom Agents" configuration
          with configure.route: '/admin/policy-manager' (see policyApi fixture) —
          that path was never registered, so it fell through the catch-all "*"
          route below and bounced straight to Login. Alias it to the real page. */}
      <Route path="/admin/policy-manager" element={<RedirectPreserveLocation to="/admin/policies?tab=rules" />} />
      <Route
        path="/admin/violations"
        element={<RedirectPreserveLocation to="/admin/policies?tab=violations" />}
      />
      <Route
        path="/admin/policy-audit"
        element={<RedirectPreserveLocation to="/security/audit-log?tab=policy-changes" />}
      />
      <Route
        path="/admin/policy-observability"
        element={<RedirectPreserveLocation to="/admin/policies?tab=observability" />}
      />
      <Route
        path="/admin/agent-behavior"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AgentBehaviorProfile />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/policy-simulation"
        element={<RedirectPreserveLocation to="/admin/policies?tab=simulation" />}
      />
      <Route
        path="/admin/custom-guardrails"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomGuardrails />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/alert-configuration"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AlertConfiguration />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/alert-configuration/subscriptions/add"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AddSubscription />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/alert-configuration/subscriptions/edit/:id"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AddSubscription />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sso"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading SSO Settings..." />}>
                <SSOSettings />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/api-keys"
        element={
          // Everyone signed in: people without manage_api_keys see only their own tokens.
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading API keys..." />}>
                <ApiKeys />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Endpoint / Shadow AI Pages — wrapped in shared date range provider */}
      <Route element={<EndpointDateRangeProvider><Outlet /></EndpointDateRangeProvider>}>
        <Route
          path="/admin/endpoint-settings"
          element={
            <ProtectedRoute requiredPermission="manage_policies" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Endpoint Settings..." />}>
                  <EndpointSettings />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/endpoint-violations"
          element={
            <ProtectedRoute requiredPermission="view_alerts" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Endpoint Violations..." />}>
                  <EndpointViolations />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fleet-overview"
          element={
            <ProtectedRoute requiredPermission="view_dashboard" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Fleet Overview..." />}>
                  <FleetOverview />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/shadow-ai-discovery"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Shadow AI Discovery..." />}>
                  <ShadowAIDiscovery />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Shadow AI (consolidated) — the `endpoint` module. Pages land here
            one phase at a time; the routes they will replace stay live until
            phase 4 turns them into redirects. */}
        <Route
          path="/endpoint-security"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading dashboard..." />}>
                  <ShadowAiOverview />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/log-explorer"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Log Explorer..." />}>
                  <ShadowAiLogExplorer />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/tool-catalog"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading AI app catalogue..." />}>
                  <ShadowAiToolCatalog />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/users"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Users..." />}>
                  <ShadowAiUsers />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/endpoints"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Endpoints..." />}>
                  <ShadowAiEndpoints />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/endpoint-security/people" element={<EndpointPeopleRedirect />} />
        <Route
          path="/endpoint-security/users/:person"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading identity..." />}>
                  <ShadowAiIdentityDetail />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/endpoints/:device"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading endpoint..." />}>
                  <ShadowAiEndpointDetail />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Adoption: the Atlas Conversations / Knowledge Gaps / Intent Analysis
            pages pinned to the Endpoint Security surface. */}
        <Route path="/endpoint-security/adoption" element={<Navigate to="/endpoint-security/adoption/conversations" replace />} />
        <Route
          path="/endpoint-security/adoption/conversations"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Conversations..." />}>
                  <AgentAtlasConversations lockedSurface="endpoint" tagline="Explore sessions and conversations with AI tools on your endpoints" />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/adoption/knowledge-gaps"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Knowledge Gaps..." />}>
                  <AgentAtlasKnowledgeGaps lockedSurface="endpoint" />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/adoption/intent-analysis"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Intent Analysis..." />}>
                  <AgentAtlasIntentAnalysis lockedSurface="endpoint" />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/dlp-analytics"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading DLP Analytics..." />}>
                  <ShadowAiDlpAnalytics />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/guardrails"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading guardrail detections..." />}>
                  <ShadowAiGuardrails />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/coverage"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Coverage..." />}>
                  <ShadowAiCoverage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/policy"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Policy..." />}>
                  {/* The ONE Policy page: Atlas Policy pinned to Endpoint, editing included
                      (decided 2026-09-29; manage screen removed 2026-10-07). */}
                  <EndpointAtlasPolicy />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/alerts"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}>
                  {isEndpointPageSwitched('alerts') ? <EndpointAtlasAlerts /> : <ShadowAiAlerts />}
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/preview/alerts"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Alerts..." />}>
                  <EndpointAtlasAlerts />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Endpoint Security's Security group: the shared Atlas security pages, pinned
            to the module's three security surfaces (endpoint, gateway, mdm) through the
            security service's "endpoint_security" group scope - the way Gemini and
            Custom Agents inherit theirs. Decided 2026-09-29. */}
        <Route element={<SurfaceScope surface="endpoint_security" emptyOnUnavailable />}>
          <Route
            path="/endpoint-security/security/signals"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading Signals..." />}>
                    <SASignals />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoint-security/security/hunt"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading Hunt..." />}>
                    <SAHunt />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoint-security/security/incidents"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading Incidents..." />}>
                    <SAIncidents />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoint-security/security/incidents/:id"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading Incident..." />}>
                    <SAIncidentDetail />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoint-security/security/skills"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading Skills..." />}>
                    <SASkills />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoint-security/security/response"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading Response..." />}>
                    <SAResponse />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoint-security/security/exceptions"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading Exceptions..." />}>
                    <SAExceptions />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoint-security/security/ai-bom"
            element={
              <ProtectedRoute requiredModule="discovery">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message="Loading AI-BOM..." />}>
                    <SAAiBom />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="/endpoint-security/compliance"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Compliance..." />}>
                  <EndpointAtlasCompliance />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/endpoint-security/audit-report"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Audit Report..." />}>
                  <EndpointAuditReport />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* The module was called Shadow AI and lived at /shadow-ai. The name
            now says what it covers — every endpoint, not just the unsanctioned
            use — and the URL follows it. The old paths keep resolving:
            bookmarks, saved links and anything a customer wrote down do not
            get renamed along with us. One splat forwards the whole subtree. */}
        <Route path="/shadow-ai/*" element={<RedirectShadowAi />} />
        {/* AI Gateway — the `ai_gateway` module. */}
        <Route
          path="/admin/ai-gateway"
          element={
            <ProtectedRoute requiredRole="Administrator" requiredModule="ai_gateway">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading AI Gateway..." />}>
                  <AIGatewayOverview />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-gateway/events"
          element={
            <ProtectedRoute requiredRole="Administrator" requiredModule="ai_gateway">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Live Events..." />}>
                  <AIGatewayLiveEvents />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-gateway/configuration"
          element={
            <ProtectedRoute requiredRole="Administrator" requiredModule="ai_gateway">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Configuration..." />}>
                  <AIGatewayConfiguration />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-gateway/keys"
          element={
            <ProtectedRoute requiredRole="Administrator" requiredModule="ai_gateway">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Keys..." />}>
                  <AIGatewayKeys />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-gateway/usage"
          element={
            <ProtectedRoute requiredRole="Administrator" requiredModule="ai_gateway">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Usage..." />}>
                  <AIGatewayUsage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-gateway/policy"
          element={
            <ProtectedRoute requiredRole="Administrator" requiredModule="ai_gateway">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading Policies..." />}>
                  <AIGatewayPolicy />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Managed Devices (MDM) — Shadow AI from Jamf + Intune inventory.
            The `mdm` module; the read API and UI ship ahead of the ingestor. `/admin/mdm` redirects to the
            overview so the bare prefix is never a dead route. */}
        <Route path="/admin/mdm" element={<Navigate to="/admin/mdm/overview" replace />} />
        {[
          ['overview', MdmOverview, 'Overview'],
          ['devices', MdmDevices, 'Managed Devices'],
          ['software', MdmSoftware, 'AI Software'],
          ['mcp', MdmMcp, 'MCP & Local Models'],
          ['policy', MdmPolicy, 'Policy & Remediation'],
        ].map(([slug, Component, label]) => (
          <Route
            key={slug}
            path={`/admin/mdm/${slug}`}
            element={
              <ProtectedRoute requiredRole="Administrator" requiredModule="mdm">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message={`Loading ${label}...`} />}>
                    <Component />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
        ))}
        {/* EDR — CrowdStrike Falcon + Microsoft Defender for Endpoint.
            The `edr` module. Connect under Management → Integrations → EDR.
            Collectors are backend services (like jamf/intune), not sidebar pages. */}
        <Route path="/admin/edr" element={<Navigate to="/admin/edr/overview" replace />} />
        {/* Legacy paths from the first EDR shell */}
        <Route path="/admin/edr/ingestor" element={<Navigate to="/admin/edr/overview" replace />} />
        <Route path="/admin/edr/integrations" element={<Navigate to={{ pathname: '/admin/integrations', search: '?category=edr' }} replace />} />
        {[
          ['overview', EdrOverview, 'EDR Overview'],
          ['endpoints', EdrEndpoints, 'EDR Endpoints'],
          ['processes', EdrProcesses, 'AI Processes'],
          ['extensions', EdrExtensions, 'Browser Extensions'],
          ['mcp', EdrMcp, 'MCP & Local Models'],
          ['alerts', EdrAlerts, 'EDR Alerts'],
        ].map(([slug, Component, label]) => (
          <Route
            key={slug}
            path={`/admin/edr/${slug}`}
            element={
              <ProtectedRoute requiredRole="Administrator" requiredModule="edr">
                <AppLayout>
                  <Suspense fallback={<PageLoadingFallback message={`Loading ${label}...`} />}>
                    <Component />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
        ))}
        <Route
          path="/admin/ai-conversations"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading AI Conversations..." />}>
                  <AIConversations />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dlp-analytics"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading DLP Analytics..." />}>
                  <DLPAnalytics />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/endpoint-user-activity"
          element={
            <ProtectedRoute requiredPermission="view_logs" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading User Activity..." />}>
                  <EndpointUserActivity />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-tool-catalog"
          element={
            <ProtectedRoute requiredPermission="view_policies" requiredPermissionModule="policy" requiredModule="discovery">
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback message="Loading AI Tool Catalog..." />}>
                  <AIToolCatalog />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/policy-audit-log"
          element={<RedirectPreserveLocation to="/security/audit-log?tab=policy-changes" />}
        />
      </Route>
      <Route
        path="/admin/audit-log"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Audit Log..." />}>
                <AdminAuditLog />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-provider"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading AI provider..." />}>
                <AiProvider />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/data-policy"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Data Policy..." />}>
                <DataPolicy />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Integrations..." />}>
                <IntegrationsSettings />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Compliance Routes */}
      {/* Compliance and its Audit Report: two pages, the two rows of the sidebar's Compliance
          group as in every module (an old ?tab=audit-report link lands on the report). */}
      <Route
        path="/admin/compliance"
        element={
          <ProtectedRoute requiredModule="governance">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomAgentsCompliance />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-report"
        element={
          <ProtectedRoute requiredModule="governance">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback message="Loading Audit Report..." />}>
                <CustomAgentsAuditReport />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Security Routes */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <SecurityDashboard />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/threat-control"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <ThreatControlCenter />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Data Security is the engine-backed Security Atlas page, scoped as the legacy page was (the
          custom agents' traces) - the same mount as /custom-agents/security/data-security. The legacy
          page counted every PII entity (1,714 on sandbox 2026-10-16) under the label the engine uses
          for records with personal data (127). */}
      <Route
        path="/security/data-security"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <SurfaceScope surface={CUSTOM_AGENTS_MODULE.securitySurface} emptyOnUnavailable>
                  <SADataSecurity traceDetail />
                </SurfaceScope>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/threat-intelligence"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <ThreatIntelligence />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/attack-path"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AttackPath />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/incidents"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <IncidentResponse />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/posture"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <SecurityPosture />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/audit-log"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <CustomAgentsAuditLog />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/compliance-evidence"
        element={
          <ProtectedRoute requiredModule="security">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <ComplianceEvidence />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Documentation moved to the external docs portal; old links and bookmarks forward there. */}
      <Route path="/docs" element={<ExternalRedirect to={DOCS_URL} />} />
      <Route path="/docs/api" element={<ExternalRedirect to={DOCS_URL} />} />
      {/* Billing Route */}
      <Route
        path="/billing"
        element={
          <ProtectedRoute requiredRole="Administrator">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <Billing />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Global Search Route */}
      <Route
        path="/global-search"
        element={
          <ProtectedRoute requiredModule="monitoring">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <GlobalSearch />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* AI Explorer Route */}
      <Route
        path="/ai-explorer"
        element={
          <ProtectedRoute requiredAnyModule={GOVERNANCE_OR_EVAL_MODULES}>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AIExplorer />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* A conversation opened full-width from the agent panel ("Expand") */}
      <Route
        path="/ai-explorer/c/:conversationId"
        element={
          <ProtectedRoute requiredAnyModule={GOVERNANCE_OR_EVAL_MODULES}>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AIExplorer />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* AI Dashboards Routes */}
      <Route
        path="/ai-explorer/dashboards"
        element={
          <ProtectedRoute requiredAnyModule={GOVERNANCE_OR_EVAL_MODULES}>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AIDashboards />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-explorer/dashboards/:dashboardId"
        element={
          <ProtectedRoute requiredAnyModule={GOVERNANCE_OR_EVAL_MODULES}>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <AIDashboardViewer />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Red Team Scanner Route */}
      <Route
        path="/red-team-scanner"
        element={
          <ProtectedRoute requiredModule="assessment">
            <LegacyAssessmentRoute to={redTeamScannerTarget}>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <RedTeamScanner />
                </Suspense>
              </AppLayout>
            </LegacyAssessmentRoute>
          </ProtectedRoute>
        }
      />
      {/* Scheduled Scans Route */}
      <Route
        path="/red-team/scheduled-scans"
        element={
          <ProtectedRoute requiredModule="assessment">
            <LegacyAssessmentRoute to={`${CA_ASSESSMENT}/schedules`}>
              <AppLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <ScheduledScansView />
                </Suspense>
              </AppLayout>
            </LegacyAssessmentRoute>
          </ProtectedRoute>
        }
      />
      {/* Change password lives in Account settings → Sign-in and security */}
      <Route path="/change-password" element={<Navigate to="/profile/security" replace />} />
      {/* API access moved to Settings › API keys (org key and personal tokens, 2026-10-30). */}
      <Route path="/profile/api" element={<Navigate to="/admin/api-keys" replace />} />
      {/* Account settings: /profile, /profile/security (tab = path) */}
      <Route
        path="/profile/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <Profile />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* ============================================================
          v2 platform — workflow wrappers + landing pages.
          All under /v2/* prefix. v1 routes above are untouched.
          Wrappers render existing v1 page components as children with
          NO edits — the WorkflowPageWrapper only adds a chip strip above.
          ============================================================ */}
      <Route
        path="/v2/discovery/atlas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="atlas" title="Atlas">
                  <AgentAtlas />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/discovery/users"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="users" title="Users">
                  <AgentAtlasUsers />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/discovery/models"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2ModelsLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/discovery/applications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2ApplicationsLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/discovery/sources"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2SourcesLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/discovery/registry"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2RegistryLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/conversations"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="conversations" title="Unified Explorer">
                  <ConversationExplorer />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/insights/roi"
        element={
          <ProtectedRoute requiredModule="finops">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="usage-roi" title="Usage & ROI">
                  <ROIAnalysis />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/insights/knowledge-gaps"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2KnowledgeGapsLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/insights/intent"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2IntentLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/posture/threats"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="threats" title="Threats Control">
                  <ThreatControlCenter />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/posture/compliance"
        element={
          <ProtectedRoute requiredModule="governance">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="compliance" title="Compliance Center">
                  <ComplianceDashboard />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/posture/audit-report"
        element={
          <ProtectedRoute requiredModule="governance">
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="audit-report" title="Audit Report">
                  <CustomAgentsAuditReport />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/posture/access"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2AccessLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/admin/audit-log"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <WorkflowPageWrapper workflowKey="audit-log" title="Audit Log">
                  <AdminAuditLog />
                </WorkflowPageWrapper>
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/admin/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Suspense fallback={<PageLoadingFallback />}>
                <V2VendorSettingsLanding />
              </Suspense>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch-all route for 404 */}
      <Route path="*" element={<UnknownRoute />} />
    </Routes>
    </CustomAgentsTimeRange>
  );
};

export default AppRoutes;
